(function () {
  var render = window.preact.render;
  var useState = window.preact.useState;
  var useEffect = window.preact.useEffect;
  var useCallback = window.preact.useCallback;
  var useRef = window.preact.useRef;
  var html = window.preact.html;

// --- Algolia Insights helpers ---
function getAlgoliaIndex() {
  const prefix = window.algoliaShopify?.config?.index_prefix || 'shopify_';
  return `${prefix}products`;
}

function sendAlgoliaEvent(method, params) {
  if (typeof window.aa !== 'function') return;
  var cfg = window.algoliaShopify?.config;
  if (!cfg?.app_id || !cfg?.search_api_key) return;
  try {
    var enriched = Object.assign({}, params);
    if (!enriched.userToken && window.__algoliaUserToken) enriched.userToken = window.__algoliaUserToken;
    window.aa(method, enriched, {
      headers: { 'X-Algolia-Application-Id': cfg.app_id, 'X-Algolia-API-Key': cfg.search_api_key }
    });
  } catch (e) { console.error('Algolia event error:', e); }
}

// --- State ---
// Use URL params captured in the inline script (algolia-native-config.liquid)
// BEFORE the Algolia plugin's router normalises the URL and strips custom params.
// Falls back to current URL if the inline capture didn't run.
const _initialUrlParams = new URLSearchParams(window.__algoliaInitialUrlParams || window.location.search);

// --- Helpers ---
function formatPrice(val) {
  if (!val && val !== 0) return html`$0.00`;
  const parts = String(val).split(".");
  const dollars = (parts[0] || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const cents = (parts[1] || "00").padEnd(2, "0").substring(0, 2);
  return html`$${dollars}<sup>.${cents}</sup>`;
}

function getLocationStock(locations, id) {
  const loc = locations?.[id];
  if (loc == null) return null;
  const val = typeof loc === "object" && loc.value !== undefined ? loc.value
    : typeof loc === "number" ? loc
    : typeof loc === "string" ? loc
    : null;
  if (val == null) return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
}

function getGeoRadius(store) {
  const geo = window.geoLocationService;
  return geo && typeof geo.isWithinStoreRadius === "function"
    ? geo.isWithinStoreRadius(store)
    : false;
}

function asBool(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
}

function getExceptionType(hit) {
  if (hit?.template_suffix === "rental") return "rental";

  const hidePriceRaw = hit?.meta?.custom?.brand_s?.fields?.hide_price;
  const hidePrice = asBool(hidePriceRaw?.value ?? hidePriceRaw);

  return hidePrice ? "hide_price" : null;
}

function appendVariantParamIfMissing(url, variantId) {
  if (variantId == null || variantId === "") return url || "";
  const s = String(url || "");
  if (/[?&]variant=/i.test(s)) return s;
  const sep = s.includes("?") ? "&" : "?";
  return `${s}${sep}variant=${encodeURIComponent(variantId)}`;
}

function buildUrl(hit) {
  let url = hit.url || (hit.handle ? `/products/${hit.handle}` : `/products/${hit.objectID}`);
  if (window.Shopify?.routes?.root && url.startsWith("/")) {
    url = window.Shopify.routes.root + url.replace(/^\//, "");
  }
  const variantId = hit.variant_id || hit.variantId || hit.variants?.[0]?.id || hit.objectID;
  if (variantId) {
    url = appendVariantParamIfMissing(url, variantId);
  }
  return url;
}

function formatMetaKey(key) {
  if (!key) return "";

  return key
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getTodayLabel() {
  const now = new Date();
  const month = now.toLocaleString("en-US", { month: "short" });
  const day = now.getDate();
  return `Today, ${month} ${day}`;
}

const storefrontAvailabilityCache = new Map();

function normalizeLocationName(name) {
  if (!name) return "";
  const normalized = String(name).toLowerCase().trim();
  if (normalized.includes("hartville")) return "hartville";
  if (normalized.includes("middlefield")) return "middlefield";
  if (
    normalized.includes("shop") ||
    normalized.includes("online") ||
    normalized.includes("warehouse") ||
    normalized.includes("shipping") ||
    normalized.includes("ecom")
  ) {
    return "shop";
  }
  return normalized;
}

function normalizeStoreKey(value) {
  if (!value) return "";
  const normalized = String(value).toLowerCase().trim();
  if (normalized.includes("hartville")) return "hartville";
  if (normalized.includes("middlefield")) return "middlefield";
  if (
    normalized.includes("shop") ||
    normalized.includes("online") ||
    normalized.includes("warehouse") ||
    normalized.includes("shipping") ||
    normalized.includes("ecom") ||
    normalized.includes("main")
  ) {
    return "shop";
  }
  return normalized;
}

function getAssignedStoreKey() {
  try {
    const stored = localStorage.getItem("geo_assigned_store");
    if (!stored) return "";
    const parsed = JSON.parse(stored);
    const key = normalizeStoreKey(parsed?.key || parsed?.name || parsed?.title || parsed?.store);
    if (key === "hartville" || key === "middlefield" || key === "shop") return key;
  } catch (e) {}
  return "";
}

function formatPickupTime(pickUpTime) {
  if (!pickUpTime) return "2 Hrs";
  const match = String(pickUpTime).match(/(\d+)\s*(?:hour|hr|h)/i);
  if (match) return `${match[1]} Hrs`;
  return String(pickUpTime).replace(/usually ready in/i, "").trim() || "2 Hrs";
}


function getShippingEtaLabel() {
  const fromConfig =
    window.algoliaConfig?.estimatedShippingFrame ||
    window.algoliaConfig?.estimated_shipping_frame ||
    window.themeSettings?.estimated_shipping_frame ||
    "";
  if (fromConfig) return fromConfig;
  return getTodayLabel();
}

async function fetchStoreAvailabilityRows(variantId) {
  const key = String(variantId || "");
  if (!key) return [];
  if (storefrontAvailabilityCache.has(key)) {
    return storefrontAvailabilityCache.get(key);
  }

  if (window.variantAvailabilityChecker?.getVariantAvailability) {
    try {
      const data = await window.variantAvailabilityChecker.getVariantAvailability(key, false);
      const rows = Object.values(data?.locations || {}).map((loc) => ({
        location: { name: loc.name },
        available: loc.available,
        quantityAvailable: loc.quantity,
        pickUpTime: loc.pickUpTime,
      }));
      storefrontAvailabilityCache.set(key, rows);
      return rows;
    } catch (e) {}
  }

  const query = `
    query StoreAvailability($variantId: ID!) {
      node(id: $variantId) {
        ... on ProductVariant {
          storeAvailability(first: 20) {
            nodes {
              location { id name }
              available
              quantityAvailable
              pickUpTime
            }
          }
        }
      }
    }
  `;

  // Routed through the shared StorefrontClient queue so availability lookups
  // never race the discount probes for the rate-limit bucket.
  try {
    if (!window.StorefrontClient) return [];
    const json = await window.StorefrontClient.request(query, {
      variantId: `gid://shopify/ProductVariant/${key}`,
    });
    if (!json) return []; // failed/throttled — uncached so a later call retries
    const rows = json?.data?.node?.storeAvailability?.nodes || [];
    storefrontAvailabilityCache.set(key, rows);
    return rows;
  } catch (e) {
    return [];
  }
}

function fixAlgoliaSortLabel() {
  document.querySelectorAll(".ais-sort").forEach(el => {
    el.childNodes.forEach(node => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const v = node.nodeValue;
      if (!v) return;
      if (v.includes("Sorted by")) {
        node.nodeValue = v.replace("Sorted by", "Sort by");
      }
    });
  });
}

function getHeaderBadgesFromTags(hit) {
  const headerConfigStr = window.algoliaConfig?.badgeTags || "";
  const headerPriority = String(headerConfigStr || "")
    .split(",")
    .map(t => String(t || "").trim())
    .filter(Boolean);

  const rawTags = hit.tags || hit.product_tags || hit._tags || hit.tag_list || [];
  const tags = Array.isArray(rawTags)
    ? rawTags
    : typeof rawTags === "string"
      ? rawTags.split(",").map(t => t.trim())
      : [];

  const tagsByNormalized = new Map();
  tags.forEach(rawTag => {
    const original = String(rawTag || "").trim();
    if (!original) return;
    const normalized = original.toLowerCase();
    if (!tagsByNormalized.has(normalized)) {
      tagsByNormalized.set(normalized, original);
    }
  });

  return headerPriority
    .map(configTag => {
      const normalized = configTag.toLowerCase();
      const original = tagsByNormalized.get(normalized);
      if (!original) return null;
      return { tag: normalized, text: original };
    })
    .filter(Boolean);
}

function renderHeaderBadge(badge) {
  if (!badge) return null;

  if (badge.tag === "on display") {
    return html`<div class="product-badge badge-header badge-on-display">On Display</div>`;
  }

  if (badge.tag === "new") {
    return html`<div class="product-badge badge-header badge-new"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.35975 11.928L6.75 14.0625L6.14025 11.928C5.98265 11.3766 5.68717 10.8745 5.28169 10.4691C4.87621 10.0636 4.37411 9.7681 3.82275 9.6105L1.6875 9L3.822 8.39025C4.37336 8.23265 4.87546 7.93717 5.28094 7.53169C5.68642 7.12621 5.9819 6.62411 6.1395 6.07275L6.75 3.9375L7.35975 6.072C7.51735 6.62335 7.81283 7.12546 8.21831 7.53094C8.62379 7.93642 9.12589 8.2319 9.67725 8.3895L11.8125 9L9.678 9.60975C9.12665 9.76735 8.62454 10.0628 8.21906 10.4683C7.81358 10.8738 7.5181 11.3759 7.3605 11.9272L7.35975 11.928ZM13.6943 6.53625L13.5 7.3125L13.3057 6.53625C13.1946 6.09119 12.9645 5.68472 12.6402 5.36027C12.3159 5.03583 11.9095 4.80562 11.4645 4.69425L10.6875 4.5L11.4645 4.30575C11.9095 4.19438 12.3159 3.96417 12.6402 3.63973C12.9645 3.31528 13.1946 2.90881 13.3057 2.46375L13.5 1.6875L13.6943 2.46375C13.8055 2.9089 14.0357 3.31544 14.3601 3.63989C14.6846 3.96434 15.0911 4.1945 15.5363 4.30575L16.3125 4.5L15.5363 4.69425C15.0911 4.8055 14.6846 5.03566 14.3601 5.36011C14.0357 5.68456 13.8055 6.0911 13.6943 6.53625ZM12.6705 15.4252L12.375 16.3125L12.0795 15.4252C11.9967 15.1767 11.8571 14.9509 11.6718 14.7657C11.4866 14.5804 11.2608 14.4408 11.0122 14.358L10.125 14.0625L11.0122 13.767C11.2608 13.6842 11.4866 13.5446 11.6718 13.3593C11.8571 13.1741 11.9967 12.9483 12.0795 12.6998L12.375 11.8125L12.6705 12.6998C12.7533 12.9483 12.8929 13.1741 13.0782 13.3593C13.2634 13.5446 13.4892 13.6842 13.7377 13.767L14.625 14.0625L13.7377 14.358C13.4892 14.4408 13.2634 14.5804 13.0782 14.7657C12.8929 14.9509 12.7533 15.1767 12.6705 15.4252Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg> New</div>`;
  }

  if (badge.tag === "free shipping") {
    return html`<div class="product-badge badge-header badge-free-shipping"><svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.01417 10.6395C5.01417 10.9379 4.89565 11.224 4.68467 11.435C4.47369 11.646 4.18754 11.7645 3.88917 11.7645C3.59081 11.7645 3.30466 11.646 3.09368 11.435C2.8827 11.224 2.76417 10.9379 2.76417 10.6395M5.01417 10.6395C5.01417 10.3412 4.89565 10.055 4.68467 9.84402C4.47369 9.63305 4.18754 9.51452 3.88917 9.51452C3.59081 9.51452 3.30466 9.63305 3.09368 9.84402C2.8827 10.055 2.76417 10.3412 2.76417 10.6395M5.01417 10.6395H9.51417M2.76417 10.6395H1.35792C1.13415 10.6395 0.919537 10.5506 0.761303 10.3924C0.603069 10.2342 0.514174 10.0195 0.514174 9.79577V7.26452M9.51417 10.6395H11.2017M9.51417 10.6395V7.26452M0.514174 7.26452V1.53827C0.512982 1.3329 0.58802 1.13438 0.724766 0.981151C0.861511 0.827919 1.05024 0.730863 1.25442 0.708769C3.75421 0.44929 6.27414 0.44929 8.77392 0.708769C9.19767 0.752269 9.51417 1.11227 9.51417 1.53827V2.25677M0.514174 7.26452H9.51417M13.4517 10.6395C13.4517 10.9379 13.3331 11.224 13.1222 11.435C12.9112 11.646 12.625 11.7645 12.3267 11.7645C12.0283 11.7645 11.7422 11.646 11.5312 11.435C11.3202 11.224 11.2017 10.9379 11.2017 10.6395M13.4517 10.6395C13.4517 10.3412 13.3331 10.055 13.1222 9.84402C12.9112 9.63305 12.625 9.51452 12.3267 9.51452C12.0283 9.51452 11.7422 9.63305 11.5312 9.84402C11.3202 10.055 11.2017 10.3412 11.2017 10.6395M13.4517 10.6395H14.2954C14.7612 10.6395 15.1422 10.2615 15.1129 9.79652C14.9622 7.31975 14.1281 4.93319 12.7032 2.90177C12.5675 2.71148 12.3902 2.55453 12.185 2.44279C11.9797 2.33105 11.7517 2.26744 11.5182 2.25677H9.51417M9.51417 2.25677V7.26452" stroke="white" stroke-width="1.02832" stroke-linecap="round" stroke-linejoin="round"/></svg> Free Shipping</div>`;
  }

  if (badge.tag === "made in usa") {
    return html`<div class="product-badge badge-header badge-in-usa">Made in USA</div>`;
  }

  return html`<div class="product-badge badge-header">${badge.text}</div>`;
}

function parseBadgeFooterValues(source) {
  if (source == null) return [];

  const values = Array.isArray(source) ? source : [source];
  const parsed = [];
  const seen = new Set();

  values.forEach(entry => {
    const rawValue = typeof entry === "object" && entry !== null && "value" in entry
      ? entry.value
      : entry;

    if (rawValue == null) return;

    const chunks = String(rawValue)
      .split(/\r?\n|,/)
      .map(item => item.trim())
      .filter(Boolean);

    chunks.forEach(text => {
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      parsed.push(text);
    });
  });

  return parsed;
}

function getFooterBadgesFromMetafield(hit) {
  const source =
    hit?.meta?.custom?.badge_footer ??
    hit?.badge_footer ??
    hit?.metafields?.custom?.badge_footer;

  return parseBadgeFooterValues(source);
}

function getActiveFacetAttributes() {
  const lastResults = algoliaShopify?.instantsearch?.search?.helper?.lastResults;
  return lastResults?.renderingContent?.facetOrdering?.facets?.order ?? [];
}

function getDynamicFeatures(hit, activeFacetAttrs = [], limit = 3) {
  const EXCLUDED_ATTRS = ["price", "price_range", "vendor", "brand", "tags", "features"];
  const features = [];

  for (const attr of activeFacetAttrs) {
    if (features.length >= limit) break;
    const attrKey = attr.split(".").pop().toLowerCase();
    if (EXCLUDED_ATTRS.includes(attrKey)) continue;

    const raw = attr.split(".").reduce((obj, key) => obj?.[key], hit);
    if (raw === undefined || raw === null || raw === "") continue;

    const label = formatMetaKey(attr.split(".").pop());

    if (Array.isArray(raw)) {
      const first = raw[0];
      if (typeof first === "string" && first) {
        features.push({ label, value: first });
      }
      continue;
    }

    if (typeof raw === "object") {
      if (raw.value !== undefined && raw.value !== null && raw.value !== "") {
        features.push({ label, value: String(raw.value) });
      }
      continue;
    }

    features.push({ label, value: String(raw) });
  }

  if (features.length < limit) {
    const specs = hit?.meta?.custom?.othertechspecs;
    if (typeof specs === "string" && specs.trim() !== "" && specs.trim() !== "|") {
      const pairs = specs.split("|").map(s => s.trim()).filter(Boolean);
      for (const pair of pairs) {
        if (features.length >= limit) break;
        const idx = pair.indexOf(":");
        if (idx > 0) {
          const specLabel = pair.substring(0, idx).trim();
          const specValue = pair.substring(idx + 1).trim();
          if (specLabel && specValue) {
            features.push({ label: specLabel, value: specValue });
          }
        }
      }
    }
  }

  return features;
}


const SVG_GRID = `<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="11" height="11" rx="1" fill="currentColor"/><rect x="14.2051" width="11" height="11" rx="1" fill="currentColor"/><rect y="14.2051" width="11" height="11" rx="1" fill="currentColor"/><rect x="14.2051" y="14.2051" width="11" height="11" rx="1" fill="currentColor"/></svg>`;
const SVG_LIST = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="4" height="4" fill="currentColor"/><rect x="5" width="20" height="4" fill="currentColor"/><rect y="7" width="4" height="4" fill="currentColor"/><rect x="5" y="7" width="20" height="4" fill="currentColor"/><rect y="14" width="4" height="4" fill="currentColor"/><rect x="5" y="14" width="20" height="4" fill="currentColor"/><rect y="21" width="4" height="4" fill="currentColor"/><rect x="5" y="21" width="20" height="4" fill="currentColor"/></svg>`;

let toggleIconsReplaced = false;
function replaceToggleIcons() {
  if (toggleIconsReplaced) return;
  const grid = document.querySelector(".ais-change-display-block");
  const list = document.querySelector(".ais-change-display-list");
  const wrapper = document.querySelector(".ais-change-display");
  if (!grid || !list || !wrapper) return;
  grid.innerHTML = SVG_GRID;
  list.innerHTML = SVG_LIST;
  wrapper.style.opacity = "1";
  toggleIconsReplaced = true;
}

let viewToggleInitialized = false;
function setupViewToggle() {
  if (viewToggleInitialized) return;
  const hitsContainer = document.querySelector(".ais-hits-container");
  if (!hitsContainer) return;

  const syncClass = () => {
    const list = hitsContainer.querySelector(".ais-Hits-list");
    if (!list) return;
    list.classList.toggle("list-view", hitsContainer.classList.contains("ais-results-as-list"));
  };

  new MutationObserver(syncClass).observe(hitsContainer, { attributes: true, attributeFilter: ["class"] });
  viewToggleInitialized = true;
}

function hideZeroReviewWidgets() {
  document.querySelectorAll("#hits-products .product-rating").forEach(wrapper => {
    const countEl = wrapper.querySelector(".yotpo-sr-bottom-line-text--right-panel");
    if (!countEl) return;
    const count = parseInt(countEl.textContent.trim(), 10);
    if (!isNaN(count) && count === 0) {
      const widget = wrapper.querySelector(".yotpo-widget-instance");
      if (widget) widget.style.display = "none";
    }
    wrapper.style.opacity = "1";
  });
}

let __yotpoObserverStarted = false;
let __yotpoInitTimer = null;
let __yotpoInitScheduledAt = 0;

function yotpoRatingsRendered() {
  // Signal that star rating widgets have been injected by Yotpo.
  return (
    document.querySelectorAll("#hits-products .mct-item-stars-rating").length > 0 ||
    document.querySelectorAll("#hits-products .yotpo-sr-star-ratings-widget").length > 0 ||
    document.querySelectorAll("#hits-products .yotpo-sr-bottom-line-text--right-panel").length > 0
  );
}

function scheduleYotpoInit(reason) {
  void reason;

  if (__yotpoInitTimer) {
    clearTimeout(__yotpoInitTimer);
  }

  __yotpoInitScheduledAt = Date.now();
  __yotpoInitTimer = setTimeout(() => {
    const container = window.yotpoWidgetsContainer;
    if (!container) return;

    try {
      container.initWidgets();
    } catch (e) {}

    // One delayed fallback for the cases where initWidgets injects slowly.
    setTimeout(() => {
      // If we still don't see ratings, try initWidgets once more.
      if (!yotpoRatingsRendered()) {
        try {
          container.initWidgets();
        } catch (e) {}
      }
      hideZeroReviewWidgets();
    }, 2500);
  }, 300);
}

function startYotpoObserverOnce() {
  if (__yotpoObserverStarted) return;
  __yotpoObserverStarted = true;

  const observerTarget = document.body;
  const obs = new MutationObserver(mutations => {
    let shouldInit = false;

    for (const m of mutations) {
      if (!m.addedNodes || m.addedNodes.length === 0) continue;

      for (const node of m.addedNodes) {
        if (!node || node.nodeType !== 1) continue;

        if (node.matches && node.matches("#hits-products .yotpo-widget-instance")) {
          shouldInit = true;
          break;
        }

        if (node.querySelector && node.querySelector("#hits-products .yotpo-widget-instance")) {
          shouldInit = true;
          break;
        }
      }

      if (shouldInit) break;
    }

    if (shouldInit) {
      scheduleYotpoInit("dom-yotpo-widget-added");
    }
  });

  obs.observe(observerTarget, { childList: true, subtree: true });
}

function refreshYotpo() {
  startYotpoObserverOnce();

  if (!window.yotpoWidgetsContainer) {
    // Yotpo container can load after the first Algolia render.
    if (!window.__yotpoWidgetsReadyListenerAttached) {
      window.__yotpoWidgetsReadyListenerAttached = true;
      document.addEventListener(
        "yotpoWidgetsContainerReady",
        () => {
          try {
            window.__yotpoWidgetsReadyListenerAttached = false;
            refreshYotpo();
          } catch (e) {}
        },
        { once: true }
      );
    }
    return;
  }

  scheduleYotpoInit("manual-refresh");
}

// Cache so each product handle is fetched at most once per page session.
const _productImagesCache = new Map();
function fetchProductImages(handle) {
  if (!handle) return Promise.resolve([]);
  if (_productImagesCache.has(handle)) return _productImagesCache.get(handle);
  const p = fetch(`/products/${handle}.json`)
    .then(r => r.json())
    .then(d => (d.product?.images || []).map(i => i.src).filter(Boolean))
    .catch(() => []);
  _productImagesCache.set(handle, p);
  return p;
}

const ALGOLIA_PLP_PAYMENT_TERMS_SECTION = "algolia-plp-payment-terms";

function pickAlgoliaPlpPaymentTermsHtml(json) {
  if (!json || typeof json !== "object") return "";
  if (json[ALGOLIA_PLP_PAYMENT_TERMS_SECTION]) return json[ALGOLIA_PLP_PAYMENT_TERMS_SECTION];
  const keys = Object.keys(json);
  const match = keys.find(
    (k) =>
      k === ALGOLIA_PLP_PAYMENT_TERMS_SECTION ||
      k.includes(ALGOLIA_PLP_PAYMENT_TERMS_SECTION)
  );
  return match ? json[match] : "";
}

function extractAlgoliaPlpPaymentTermsInner(htmlFragment) {
  if (!htmlFragment) return "";
  const doc = new DOMParser().parseFromString(htmlFragment, "text/html");
  const root = doc.querySelector(".algolia-plp-payment-terms-root");
  if (root) return root.innerHTML;
  const section = doc.querySelector('[id^="shopify-section-"]');
  if (section) {
    const innerRoot = section.querySelector(".algolia-plp-payment-terms-root");
    return innerRoot ? innerRoot.innerHTML : section.innerHTML;
  }
  return htmlFragment;
}

function getAlgoliaPlpPaymentTermsCache() {
  if (!window.__algoliaPlpPaymentTermsCache) window.__algoliaPlpPaymentTermsCache = {};
  return window.__algoliaPlpPaymentTermsCache;
}

// Pick a PLP card's main image. Algolia's `distinct` returns one representative
// VARIANT record per product, and the card deep-links to THAT variant
// (?variant=objectID). So the card image must be the representative variant's own
// image (hit.image) — otherwise the photo shown on the card won't match the image
// the PDP shows after the click. Fall back to the product image only when the
// variant has none. NOTE: kept in sync with the same helper in assets/plp-app.js.
function pickPlpCardImage(hit) {
  if (!hit) return "";
  return hit.image || hit.product_image || "";
}

// --- Components ---
function ProductCard({ hit, activeFacetAttrs = [] }) {
  const [qty, setQty] = useState(1);
  const [slideIndex, setSlideIndex] = useState(0);
  const mainImage = pickPlpCardImage(hit);
  const [images, setImages] = useState(mainImage ? [mainImage] : []);
  const swipeStartX = useRef(null);
  const barRef = useRef(null);
  const installmentHostRef = useRef(null);

  function onSwipeStart(e) {
    swipeStartX.current = e.touches ? e.touches[0].clientX : e.clientX;
  }
  function onSwipeEnd(e) {
    if (swipeStartX.current === null) return;
    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diff = swipeStartX.current - endX;
    if (Math.abs(diff) > 30) {
      setSlideIndex(prev =>
        diff > 0 ? Math.min(prev + 1, images.length - 1) : Math.max(prev - 1, 0)
      );
    }
    swipeStartX.current = null;
  }

  function onProductClick() {
    const queryID = hit.__queryID;
    if (!queryID || !hit.objectID) return;
    sendAlgoliaEvent('clickedObjectIDsAfterSearch', {
      index: getAlgoliaIndex(),
      eventName: 'Product Clicked',
      queryID,
      objectIDs: [hit.objectID],
      positions: [hit.__position],
    });
    try {
      sessionStorage.setItem('algolia_last_click', JSON.stringify({
        queryID, objectID: hit.objectID, indexName: getAlgoliaIndex(), timestamp: Date.now()
      }));
    } catch (e) {}
    // Persist click attribution for the custom pixel purchase tracking
    try {
      const key = 'algolia_analytics_clicked_objects';
      const clicks = JSON.parse(localStorage.getItem(key) || '[]');
      clicks.push({ index: getAlgoliaIndex(), queryID, objectIDs: [hit.objectID] });
      localStorage.setItem(key, JSON.stringify(clicks));
    } catch (e) {}
  }

  function onThumbPointerDown(e) {
    e.preventDefault();
    e.stopPropagation();
    const bar = barRef.current;
    if (!bar) return;

    function move(ev) {
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const { left, width } = bar.getBoundingClientRect();
      const idx = Math.min(
        Math.floor(((clientX - left) / width) * images.length),
        images.length - 1
      );
      setSlideIndex(Math.max(0, idx));
    }
    function up() {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", up);
    }
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    document.addEventListener("touchmove", move, { passive: true });
    document.addEventListener("touchend", up);
    move(e);
  }

  useEffect(() => {
    fetchProductImages(hit.handle).then(imgs => {
      if (imgs.length > 1) {
        // Put the variant image first so slideIndex 0 shows the correct one
        const variantSrc = mainImage.replace(/\?.*$/, "");
        const idx = imgs.findIndex(src => src.replace(/\?.*$/, "") === variantSrc);
        if (idx > 0) {
          const reordered = [imgs[idx], ...imgs.slice(0, idx), ...imgs.slice(idx + 1)];
          setImages(reordered);
        } else {
          setImages(imgs);
        }
      }
    });
  }, [hit.handle]);



  // --- Automatic discount integration (from algolia-discount-checker) ---
  const [discountInfo, setDiscountInfo] = useState(null);
  const [pricesReady, setPricesReady] = useState(() => !!window.__discountsReady);
  const currentVariantId = String(hit.variant_id || hit.variantId || hit.variants?.[0]?.id || hit.objectID);

  useEffect(() => {
    // Re-check global cache when variant changes (handles preact component reuse)
    const cached = window.__algoliaDiscounts?.get(currentVariantId);
    setDiscountInfo(cached?.hasDiscount ? cached : null);

    function onDiscount(e) {
      if (String(e.detail.variantId) === currentVariantId && e.detail.discountInfo?.hasDiscount) {
        setDiscountInfo(e.detail.discountInfo);
      }
    }

    function onReady() { setPricesReady(true); }

    document.addEventListener('algolia.discount.resolved', onDiscount);
    if (!window.__discountsReady) {
      document.addEventListener('algolia.discounts.ready', onReady);
    }
    return () => {
      document.removeEventListener('algolia.discount.resolved', onDiscount);
      document.removeEventListener('algolia.discounts.ready', onReady);
    };
  }, [currentVariantId]);

  const outOfStock = !hit.inventory_available || hit.inventory_available <= 0;
  const algoliaOnSale = !!(hit.compare_at_price && parseFloat(hit.compare_at_price) > parseFloat(hit.price || 0));
  const exceptionType = getExceptionType(hit);
  const isException = !!exceptionType;
  const exceptionLabel = exceptionType === "hide_price" ? "Call for Price!" : "Rental Item";

  // Determine effective prices: discount checker can override Algolia prices
  let effectivePrice = hit.price || "0.00";
  let effectiveCompareAt = hit.compare_at_price;
  let isOnSale = algoliaOnSale;

  if (discountInfo) {
    const algoliaPrice = parseFloat(hit.price || 0);
    // Only apply automatic discount if it yields a lower price than what Algolia shows
    if (discountInfo.finalPrice < algoliaPrice) {
      effectivePrice = discountInfo.finalPrice.toFixed(2);
      effectiveCompareAt = discountInfo.originalPrice.toFixed(2);
      isOnSale = true;
    } else if (!algoliaOnSale) {
      // Product is not on sale per Algolia but has an automatic discount
      effectivePrice = discountInfo.finalPrice.toFixed(2);
      effectiveCompareAt = discountInfo.originalPrice.toFixed(2);
      isOnSale = true;
    }
  }

  const showPriceSkeleton = !pricesReady && !isException;

  const url = buildUrl(hit);
  const variantId = hit.variant_id || hit.variantId || hit.variants?.[0]?.id || hit.objectID;
  const image = images[slideIndex] || mainImage;
  const brand = hit.meta?.custom?.brand || hit.vendor || "";
  const warrantyPage = hit.meta?.custom?.warranty_page || "";
  const dynamicFeatures = getDynamicFeatures(hit, activeFacetAttrs);

  const headerBadges = getHeaderBadgesFromTags(hit);
  const footerBadges = getFooterBadgesFromMetafield(hit);

  const isGiftCard = window.PlpIsGiftCardProduct
    ? window.PlpIsGiftCardProduct({
        tags: hit.tags,
        productType: hit.product_type,
        handle: hit.handle,
        title: hit.title,
      })
    : (hit.tags || []).includes('Gift Cards') || String(hit.product_type || '').toLowerCase().includes('gift card');
  const showBuyLater = !!(window.algoliaConfig?.showBuyLater) && !isGiftCard;
  const showWarranty = !!(window.algoliaConfig?.showWarranty) && !isGiftCard;

  const locs = hit.locations_inventory || {};
  const hartville = getLocationStock(locs, "81644978339");
  const middlefield = getLocationStock(locs, "81645011107");
  const warehouse = getLocationStock(locs, "79150579875");
  const hasHartville = hartville !== null && hartville > 0;
  const hasMiddlefield = middlefield !== null && middlefield > 0;
  const hasWarehouse = warehouse !== null && warehouse > 0;
  const isInStoreOnly = (hasHartville || hasMiddlefield) && warehouse === null;
  const isNearHartville = getGeoRadius("hartville");
  const isNearMiddlefield = getGeoRadius("middlefield");
  const [deliveryOptions, setDeliveryOptions] = useState({
    showShipping: hasWarehouse,
    shippingText: getShippingEtaLabel(),
    showHartville: hasHartville && isNearHartville,
    hartvilleText: "2 Hours",
    showMiddlefield: hasMiddlefield && isNearMiddlefield,
    middlefieldText: "2 Hours",
  });

  const favoritesUrl = `${window.Shopify?.routes?.root || ""}pages/favorites`;

  useEffect(() => {
    let mounted = true;

    async function resolveDeliveryOptions() {
      const rows = await fetchStoreAvailabilityRows(variantId);
      if (!mounted || !rows.length) return;

      let shopLocation = null;
      let hartvilleLocation = null;
      let middlefieldLocation = null;

      rows.forEach((row) => {
        const normalized = normalizeLocationName(row?.location?.name);
        if (normalized === "shop" && !shopLocation) shopLocation = row;
        if (normalized === "hartville" && !hartvilleLocation) hartvilleLocation = row;
        if (normalized === "middlefield" && !middlefieldLocation) middlefieldLocation = row;
      });

      const hasShopStock = !!(
        shopLocation &&
        ((shopLocation.quantityAvailable !== null && shopLocation.quantityAvailable > 0) || shopLocation.available)
      );
      const hasHartvilleStock = !!(
        hartvilleLocation &&
        ((hartvilleLocation.quantityAvailable !== null && hartvilleLocation.quantityAvailable > 0) || hartvilleLocation.available)
      );
      const hasMiddlefieldStock = !!(
        middlefieldLocation &&
        ((middlefieldLocation.quantityAvailable !== null && middlefieldLocation.quantityAvailable > 0) || middlefieldLocation.available)
      );

      const assignedStoreKey = getAssignedStoreKey();
      const showHartvilleByContext = assignedStoreKey
        ? assignedStoreKey === "hartville"
        : isNearHartville;
      const showMiddlefieldByContext = assignedStoreKey
        ? assignedStoreKey === "middlefield"
        : isNearMiddlefield;

      setDeliveryOptions({
        showShipping: hasShopStock,
        shippingText: getShippingEtaLabel(),
        showHartville: hasHartvilleStock && showHartvilleByContext,
        hartvilleText: formatPickupTime(hartvilleLocation?.pickUpTime),
        showMiddlefield: hasMiddlefieldStock && showMiddlefieldByContext,
        middlefieldText: formatPickupTime(middlefieldLocation?.pickUpTime),
      });
    }

    resolveDeliveryOptions();
    return () => {
      mounted = false;
    };
  }, [variantId, isNearHartville, isNearMiddlefield]);

  useEffect(() => {
    if (!showBuyLater || !hit?.handle || !variantId) return;
    const el = installmentHostRef.current;
    if (!el) return;
    let cancelled = false;
    const cache = getAlgoliaPlpPaymentTermsCache();
    const cacheKey = `${hit.handle}::${variantId}`;
    if (cache[cacheKey]) {
      el.innerHTML = cache[cacheKey];
      const runBnpl = () => window.styleShopifyPaymentTerms?.();
      requestAnimationFrame(runBnpl);
      [150, 450, 1200].forEach((ms) => setTimeout(runBnpl, ms));
      return;
    }
    const root = String(window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
    const reqUrl = `${root}products/${encodeURIComponent(hit.handle)}?sections=${encodeURIComponent(ALGOLIA_PLP_PAYMENT_TERMS_SECTION)}&variant=${encodeURIComponent(String(variantId))}`;
    fetch(reqUrl, { credentials: "same-origin" })
      .then((r) => {
        if (!r.ok) throw new Error("payment terms section");
        return r.json();
      })
      .then((json) => {
        if (cancelled || !el) return;
        const raw = pickAlgoliaPlpPaymentTermsHtml(json);
        const inner = extractAlgoliaPlpPaymentTermsInner(raw);
        if (!inner || inner.trim().length < 20) {
          throw new Error("empty payment terms");
        }
        el.innerHTML = inner;
        cache[cacheKey] = inner;
        const runBnpl = () => window.styleShopifyPaymentTerms?.();
        requestAnimationFrame(runBnpl);
        [150, 450, 1200].forEach((ms) => setTimeout(runBnpl, ms));
      })
      .catch(() => {
        if (cancelled || !el) return;
        el.textContent = "";
        const wrap = document.createElement("div");
        wrap.className = "product-buy-later product-buy-later--fallback";
        const a = document.createElement("a");
        a.href = buildUrl(hit);
        a.className = "product-buy-later-cta";
        a.textContent = "View financing options";
        wrap.appendChild(a);
        el.appendChild(wrap);
      });
    return () => {
      cancelled = true;
    };
  }, [showBuyLater, hit.handle, variantId]);

  async function addToCart(e) {
    if (outOfStock) return;
    const btn = e?.currentTarget;
    const span = btn?.querySelector("span");
    const originalText = span?.textContent;
    if (btn) btn.disabled = true;
    if (span) span.textContent = "Adding...";

    try {
      const cartDrawer = document.querySelector("cart-drawer");
      const body = { id: variantId, quantity: qty };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        body.sections = cartDrawer.getSectionsToRender().map(s => s.id);
        body.sections_url = window.location.pathname;
        cartDrawer.setActiveElement(document.activeElement);
      }

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (data.status) {
        console.error("Add to cart error:", data);
        if (span) span.textContent = "Error";
        setTimeout(() => { if (btn) btn.disabled = false; if (span) span.textContent = originalText; }, 2000);
        return;
      }

      if (span) span.textContent = "Added!";

      // Send Algolia conversion event
      if (hit.__queryID && hit.objectID) {
        sendAlgoliaEvent('addedToCartObjectIDsAfterSearch', {
          index: getAlgoliaIndex(),
          eventName: 'Product Added to Cart after Search',
          queryID: hit.__queryID,
          objectIDs: [hit.objectID],
        });
        window.__algoliaAtcSent = true;
        try { sessionStorage.removeItem('algolia_last_click'); } catch (e) {}
        // Persist click attribution for the custom pixel purchase tracking (quick-add from PLP)
        try {
          const key = 'algolia_analytics_clicked_objects';
          const clicks = JSON.parse(localStorage.getItem(key) || '[]');
          clicks.push({ index: getAlgoliaIndex(), queryID: hit.__queryID, objectIDs: [hit.objectID] });
          localStorage.setItem(key, JSON.stringify(clicks));
        } catch (e) {}
      }

      if (typeof window.publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: "algolia-search",
          productVariantId: variantId,
          cartData: data
        });
      }

      if (cartDrawer && data.sections) {
        if (cartDrawer.classList.contains("is-empty")) {
          cartDrawer.classList.remove("is-empty");
          cartDrawer.querySelector(".drawer__inner-empty")?.remove();
        }
        try { cartDrawer.renderContents(data); } catch {}
      }

      window.dispatchEvent(new CustomEvent("cart:updated", { detail: { cartData: data } }));

      setTimeout(() => { if (btn) btn.disabled = false; if (span) span.textContent = originalText; }, 1500);
    } catch (err) {
      console.error("Add to cart failed", err);
      if (btn) btn.disabled = false;
      if (span) span.textContent = originalText;
    }
  }

  return html`
    <article class="product-card" data-product-id=${hit.objectID}>

      <div class="product-image-column">
        <div
          class="product-image-wrapper"
          onTouchStart=${onSwipeStart}
          onTouchEnd=${onSwipeEnd}
          onMouseDown=${onSwipeStart}
          onMouseUp=${onSwipeEnd}
        >
          <a href=${url} class="product-image" onClick=${onProductClick}>
            ${image && html`<img src=${image} alt=${hit.title} draggable="false" />`}
          </a>
          <div class="product-badges-header">
            ${outOfStock && html`<div class="product-badge badge-header badge-soldout">Sold Out</div>`}
            ${!outOfStock && pricesReady && isOnSale && html`<div class="product-badge badge-header badge-sale"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.176 2.25H3.9375C3.48995 2.25 3.06072 2.42779 2.74426 2.74426C2.42779 3.06072 2.25 3.48995 2.25 3.9375V7.176C2.25 7.62375 2.42775 8.0535 2.74425 8.36925L9.93 15.555C10.4542 16.0792 11.265 16.209 11.8853 15.8025C13.4465 14.78 14.78 13.4465 15.8025 11.8853C16.209 11.265 16.0792 10.4542 15.555 9.93L8.37 2.745C8.21327 2.58808 8.02715 2.46359 7.82227 2.37865C7.61739 2.29372 7.39778 2.25 7.176 2.25Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.5 4.5H4.5066V4.5066H4.5V4.5Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/></svg> Sale</div>`}
            ${isInStoreOnly && html`<div class="product-badge badge-header badge-in-store-only"><img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/icon-store-badge.png?v=1767968126" />IN-STORE ONLY</div>`}
            ${headerBadges.map(renderHeaderBadge)}
          </div>
          <div class="product-badges-footer">
            ${footerBadges.length > 0 && html`
              <div class="product-badge badge-footer badge-footer-icon">
                <img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/icon-user.png?v=1768579014" alt="User" />
              </div>
            `}
            ${footerBadges.map(text => html`
              <div class="product-badge badge-footer">${text}</div>
            `)}
          </div>
          <a
            href=${favoritesUrl}
            class="add-to-favorites wishlist-button product-bookmark"
            data-variant-id=${variantId}
            data-product-url=${url}
            data-product-handle=${hit.handle || ""}
            data-product-brand=${brand}
            data-product-title=${hit.title || ""}
            data-product-price=${hit.price ? 100 * parseFloat(hit.price) : 0}
            data-product-compare-price=${hit.compare_at_price ? 100 * parseFloat(hit.compare_at_price) : 0}
            data-product-image=${image}
            data-product-image-alt=${hit.title || ""}
            aria-label="Save product"
          >
            <svg width="20" height="23" viewBox="0 0 20 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.0464 1.14868C17.3297 1.29802 18.2712 2.40518 18.2712 3.69785V21.773L9.52124 17.398L0.77124 21.773V3.69785C0.77124 2.40518 1.71157 1.29802 2.99607 1.14868C7.33163 0.645426 11.7109 0.645426 16.0464 1.14868Z" stroke="#1B5461" stroke-width="1.54247" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
        ${images.length > 1 && html`
          <div class="image-slider-bar" ref=${barRef}>
            <div
              class="image-slider-thumb"
              style=${{
                width: `${100 / images.length}%`,
                left: `${(slideIndex * 100) / images.length}%`
              }}
              onPointerDown=${onThumbPointerDown}
              onTouchStart=${onThumbPointerDown}
            />
          </div>
        `}
      </div>

      <div class="product-info-container">

        <div class="product-info-column">
          <div class="product-header-info">
            <p class="product-brand">${brand}</p>
            <a href=${url} class="product-title-link" onClick=${onProductClick}>
              <h3 class="product-title">${hit.title}</h3>
            </a>
          </div>
          <div class="product-price-section product-price-section-mobile ${isOnSale ? "product-price-compare-section" : ""}">
            ${isException
              ? html`<span class="product-price rental-label">${exceptionLabel}</span>`
              : showPriceSkeleton
                ? html`<span class="product-price-skeleton"></span>`
                : html`
                  <span class="product-price">${formatPrice(effectivePrice)}</span>
                  ${isOnSale && html`<span class="product-price-compare">${formatPrice(effectiveCompareAt)}</span>`}
                `
            }
          </div>
          <div class="product-rating">
            <div
              class="yotpo-widget-instance"
              data-yotpo-instance-id="1217528"
              data-yotpo-product-id=${hit.id}
              data-yotpo-section-id="search"
            ></div>
          </div>
          ${(hit.meta?.custom?.short_description || hit.body_html_safe) && html`<p class="product-description">${hit.meta?.custom?.short_description || hit.body_html_safe}</p>`}
          ${dynamicFeatures.length > 0 && html`
            <div class="product-features">
              ${dynamicFeatures.map(f => html`
                <span class="feature-tag"><b>${f.label}:</b> ${f.value}</span>
              `)}
            </div>
          `}

          <div class="product-warranty-buy-later">
            ${showBuyLater && html`
              <div class="product-buy-later product-buy-later--shopify-host" ref=${installmentHostRef}></div>
            `}
            ${showWarranty && html`
              <div class="product-warranty">
                <div class="product-warranty-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 10.6251L9.375 12.5001L12.5 8.12505M10 2.26172C8.11311 4.05358 5.60022 5.03637 2.99834 5.00005C2.66737 6.00837 2.49915 7.06297 2.5 8.12422C2.5 12.7842 5.68667 16.6992 10 17.8101C14.3133 16.7001 17.5 12.7851 17.5 8.12505C17.5 7.03339 17.325 5.98255 17.0017 4.99922H16.875C14.2117 4.99922 11.7917 3.95922 10 2.26172Z" stroke="#46B93A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="product-warranty-content">
                  <p class="product-warranty-value">1 additional year</p>
                  <p class="product-warranty-label">Extended Warranty</p>
                </div>
                <a href=${warrantyPage} class="warranty-link">Learn More</a>
              </div>
            `}
          </div>
        </div>

        <div class="product-actions-column">
          <div class="product-price-section ${isOnSale ? "product-price-compare-section" : ""}">
            ${isException
              ? html`<span class="product-price rental-label">${exceptionLabel}</span>`
              : showPriceSkeleton
                ? html`<span class="product-price-skeleton"></span>`
                : html`
                  <span class="product-price">${formatPrice(effectivePrice)}</span>
                  ${isOnSale && html`<span class="product-price-compare">${formatPrice(effectiveCompareAt)}</span>`}
                `
            }
          </div>

          ${!isException && html`
                    <div class="product-delivery-options" data-variant-id=${variantId}>
            ${deliveryOptions.showShipping && html`
              <div class="delivery-option-item" data-option-type="shipping">
                <div class="delivery-option-details">
                  <div class="delivery-option-label">Shipping</div>
                  <div class="delivery-option-text">Ships <span class="delivery-option-data">${deliveryOptions.shippingText}</span></div>
                </div>
                <div class="shipping-details-icon">
                  <img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/cc969f9bcd2f6baaf178cbd6761dad2a02a93da4.png?v=1766155399" alt="Shipping" />
                </div>
              </div>
            `}
            ${deliveryOptions.showHartville && html`
              <div class="delivery-option-item" data-option-type="hartville">
                <div class="delivery-option-details">
                  <div class="delivery-option-label">Store Pickup, Hartville</div>
                  <div class="delivery-option-text">Ready Within <span class="delivery-option-data">${deliveryOptions.hartvilleText}</span></div>
                </div>
                <div class="shipping-details-icon">
                  <img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/e141ebc14978ae261fd65ddd222c0e8f09379548_1.png?v=1766155482" alt="Store Pickup" />
                </div>
              </div>
            `}
            ${deliveryOptions.showMiddlefield && html`
              <div class="delivery-option-item" data-option-type="middlefield">
                <div class="delivery-option-details">
                  <div class="delivery-option-label">Store Pickup, Middlefield</div>
                  <div class="delivery-option-text">Ready Within <span class="delivery-option-data">${deliveryOptions.middlefieldText}</span></div>
                </div>
                <div class="shipping-details-icon">
                  <img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/e141ebc14978ae261fd65ddd222c0e8f09379548_1.png?v=1766155482" alt="Store Pickup" />
                </div>
              </div>
            `}
          </div>
          `}

          ${isException ? html`
            <a href=${url} class="product-add-cart product-view-item" onClick=${onProductClick}>
              <span>View Item</span>
            </a>
          ` : html`
          <div class="product-quantity-add-to-cart">
            <div class="product-quantity-section">
              <div class="quantity-selector ${outOfStock ? "disabled" : ""}">
                <button
                  disabled=${outOfStock}
                  class="quantity-btn quantity-minus"
                  type="button"
                  onClick=${() => setQty(q => Math.max(1, q - 1))}
                >-</button>
                <input
                  disabled=${outOfStock}
                  type="number"
                  class="quantity-input"
                  value=${qty}
                  min="1"
                  data-variant-id=${variantId}
                  onInput=${e => setQty(parseInt(e.target.value) || 1)}
                />
                <button
                  disabled=${outOfStock}
                  class="quantity-btn quantity-plus"
                  type="button"
                  onClick=${() => setQty(q => q + 1)}
                >+</button>
              </div>
            </div>
            <button
              disabled=${outOfStock}
              class="product-add-cart ${outOfStock ? "disabled" : ""}"
              data-variant-id=${variantId}
              type="button"
              onClick=${addToCart}
            >
              ${!outOfStock && html`<img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/icon_cart_with_item_ac32f802-cb90-4b69-89df-a6e7ac337129.png?v=1773412852" width="22" height="22" alt="" />`}
              <span>${outOfStock ? "Sold Out" : "Add To Cart"}</span>
            </button>
          </div>
          `}
        </div>

      </div>

      <div class="product-content-grid">
        <div class="product-content-grid-header">
          <p class="product-brand">${brand}</p>
          <a href=${url} class="product-title-link" onClick=${onProductClick}>
            <h3 class="product-title">${hit.title}</h3>
          </a>
        </div>
        <div class="product-rating">
          <div
            class="yotpo-widget-instance"
            data-yotpo-instance-id="1217528"
            data-yotpo-product-id=${hit.id}
            data-yotpo-section-id="search"
          ></div>
        </div>
        <div class="product-price-and-add-to-cart-container">
          <div class="product-price-section ${isOnSale ? "product-price-compare-section" : ""}">
            ${isException
              ? html`<span class="product-price rental-label">${exceptionLabel}</span>`
              : showPriceSkeleton
                ? html`<span class="product-price-skeleton"></span>`
                : html`
                  <span class="product-price">${formatPrice(effectivePrice)}</span>
                  ${isOnSale && html`<span class="product-price-compare">${formatPrice(effectiveCompareAt)}</span>`}
                `
            }
          </div>
          ${isException ? html`
            <a href=${url} class="product-add-cart product-view-item" onClick=${onProductClick}>
              <span>View Item</span>
            </a>
          ` : html`
          <button
            disabled=${outOfStock}
            class="product-add-cart ${outOfStock ? "disabled" : ""}"
            data-variant-id=${variantId}
            type="button"
            onClick=${addToCart}
          >
            ${!outOfStock && html`<img src="https://cdn.shopify.com/s/files/1/0709/2469/7763/files/icon_cart_with_item_ac32f802-cb90-4b69-89df-a6e7ac337129.png?v=1773412852" width="14" height="14" alt="" />`}
            <span>${outOfStock ? "Sold Out" : "Add To Cart"}</span>
          </button>
          `}
        </div>
      </div>

    </article>
  `;
}

function ViewToggle({ viewMode, onToggle }) {
  return html`
    <div class="plp-toolbar__view" style="display:flex;gap:8px;margin-bottom:16px;justify-content:flex-end;">
      <button
        class="plp-toolbar__view-btn${viewMode === 'grid' ? ' plp-toolbar__view-btn--active' : ''}"
        onClick=${() => onToggle('grid')}
        aria-label="Grid view"
        style="display:flex;align-items:center;justify-content:center;width:33px;height:33px;border:1px solid var(--plp-grey,#C2C2C2);background:${viewMode === 'grid' ? 'var(--plp-beige,#F5F3ED)' : '#fff'};padding:0;border-radius:6px;cursor:pointer;"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="1" y="1" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="11" y="1" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="1" y="11" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="11" y="11" width="8" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/></svg>
      </button>
      <button
        class="plp-toolbar__view-btn${viewMode === 'list' ? ' plp-toolbar__view-btn--active' : ''}"
        onClick=${() => onToggle('list')}
        aria-label="List view"
        style="display:flex;align-items:center;justify-content:center;width:33px;height:33px;border:1px solid var(--plp-grey,#C2C2C2);background:${viewMode === 'list' ? 'var(--plp-beige,#F5F3ED)' : '#fff'};padding:0;border-radius:6px;cursor:pointer;"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="1" y="1" width="18" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="1" y="8" width="18" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="1" y="15" width="18" height="5" rx="1" stroke="currentColor" stroke-width="1.5"/></svg>
      </button>
    </div>
  `;
}

function InfiniteHits({ hits, hasMore, helper, activeFacetAttrs }) {
  const isLoadingRef = useRef(false);

  // Reset loading flag when new hits arrive
  const prevLengthRef = useRef(0);
  if (hits.length !== prevLengthRef.current) {
    prevLengthRef.current = hits.length;
    isLoadingRef.current = false;
  }

  // Scroll listener — triggers next page fetch at 75% scroll depth.
  // Uses a callback ref on the container so the listener is bound/unbound
  // synchronously during Preact's commit phase (avoids useEffect timing issues).
  const containerRef = useCallback((node) => {
    if (!node || !hasMore) return;

    function onScroll() {
      if (isLoadingRef.current || !hasMore) return;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrolled = scrollTop + viewportHeight;
      if (scrolled >= docHeight * 0.75) {
        isLoadingRef.current = true;
        window.removeEventListener("scroll", onScroll);
        helper.nextPage();
        helper.search();
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    // Check immediately in case we're already past 75%
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, helper, hits.length]);

  // View mode state (persists in localStorage)
  var viewState = useState(function() {
    try { return localStorage.getItem('plp-view-mode') || 'grid'; } catch(_) { return 'grid'; }
  });
  var viewMode = viewState[0];
  var setViewMode = viewState[1];
  function toggleView(mode) {
    setViewMode(mode);
    try { localStorage.setItem('plp-view-mode', mode); } catch(_) {}
  }

  // Yotpo star ratings for the Preact search cards. The collection PLP fetches
  // these in plp-app.js; search renders through algolia-native.js, which had no
  // equivalent — so search cards never showed stars. Mirror that logic here:
  // request the bottomline per product id once and re-render with the result.
  var ratingsState = useState({}); // productId -> { avg, count }
  var ratings = ratingsState[0];
  var setRatings = ratingsState[1];
  var ratingReqRef = useRef(null);
  if (!ratingReqRef.current) ratingReqRef.current = new Set();

  useEffect(function () {
    if (!hits || !hits.length) return;
    var YOTPO_KEY = 'bzwqO2raPh4O7OoNJaS3vueIBxaZuBnxORwqBmmi';
    hits.forEach(function (hit) {
      var pid = hit.id != null ? String(hit.id) : '';
      if (!pid || ratingReqRef.current.has(pid)) return;
      ratingReqRef.current.add(pid);
      window.fetch('https://api-cdn.yotpo.com/v1/widget/' + YOTPO_KEY + '/products/' + pid + '/reviews.json?per_page=1')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var bl = data && data.response && data.response.bottomline;
          if (!bl) return;
          var count = bl.total_review || 0;
          if (!count) return; // no reviews — leave hidden
          setRatings(function (prev) {
            var next = Object.assign({}, prev);
            next[pid] = { avg: bl.average_score || 0, count: count };
            return next;
          });
        })
        .catch(function () {});
    });
  }, [hits]);

  // A handful of live products have NO images at all in Shopify (hit.image and
  // hit.product_image both null) — an empty src renders the broken-image icon,
  // so fall back to a transparent SVG and let the card's grey container show.
  var IMG_PLACEHOLDER = 'data:image/svg+xml;utf8,' +
    encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"/>');

  // Adapt Algolia hit to our Preact card format
  function adaptHit(hit) {
    var _r = ratings[String(hit.id)];
    return {
      objectID: hit.objectID,
      title: hit.title || '',
      handle: hit.handle || '',
      // Deep-link to the representative variant (the one the card shows) so the
      // PDP opens on the searched/selected variant, not the default one.
      url: '/products/' + (hit.handle || hit.objectID) + (((hit.variant_id || hit.objectID) && (hit.variants_count || 0) > 1) ? ('?variant=' + (hit.variant_id || hit.objectID)) : ''),
      image: pickPlpCardImage(hit) || IMG_PLACEHOLDER,
      price: Math.round((hit.price || 0) * 100),
      priceMin: hit.variants_min_price != null ? Math.round(hit.variants_min_price * 100) : null,
      priceMax: hit.variants_max_price != null ? Math.round(hit.variants_max_price * 100) : null,
      compareAtPrice: hit.compare_at_price && hit.compare_at_price > 0 && hit.compare_at_price > hit.price
        ? Math.round(hit.compare_at_price * 100) : null,
      vendor: hit.vendor || '',
      productType: hit.product_type || '',
      available: hit.inventory_available !== false,
      inventoryQuantity: (function () {
        var locs = hit.locations_inventory || {};
        var total = 0;
        for (var k in locs) {
          if (locs.hasOwnProperty(k)) {
            var v = locs[k];
            var n = typeof v === 'object' && v.value !== undefined ? Number(v.value) : Number(v);
            if (!isNaN(n) && n > 0) total += n;
          }
        }
        return total > 0 ? total : null;
      })(),
      tags: hit.tags || [],
      rating: _r ? _r.avg : 0,
      ratingCount: _r ? _r.count : 0,
      description: (function () {
        var short = hit.meta && hit.meta.custom && hit.meta.custom.short_description;
        if (short) return short.replace(/<[^>]*>/g, '');
        var full = (hit.body_html_safe || '').replace(/<[^>]*>/g, '');
        return full.length > 200 ? full.substring(0, 200) + '...' : full;
      })(),
      variantId: hit.variant_id || hit.variantId || (hit.variants && hit.variants[0] && hit.variants[0].id) || hit.objectID,
      sku: hit.sku || '',
      variantsCount: hit.variants_count || 0,
    };
  }

  var useOurCards = !!window.PlpProductCardGrid;
  var GridCard = window.PlpProductCardGrid;
  var ListCard = window.PlpProductCardList;

  // Client-side sort state — no extra Algolia request needed.
  var sortState = useState('relevance');
  var sortBy = sortState[0];
  var setSortBy = sortState[1];

  function sortHits(hitsArr, sort) {
    if (sort === 'price-asc')  return hitsArr.slice().sort(function(a, b) { return (a.price || 0) - (b.price || 0); });
    if (sort === 'price-desc') return hitsArr.slice().sort(function(a, b) { return (b.price || 0) - (a.price || 0); });
    if (sort === 'newest')     return hitsArr.slice().sort(function(a, b) { return (b.created_at_i || b.published_at_i || 0) - (a.created_at_i || a.published_at_i || 0); });
    if (sort === 'name-asc')   return hitsArr.slice().sort(function(a, b) { return (a.title || '').localeCompare(b.title || ''); });
    return hitsArr; // 'relevance' — keep Algolia ranking order
  }

  function handleSortChange(value) {
    setSortBy(value);
  }

  var displayedHits = sortHits(hits, sortBy);

  function handleAddToCart(variantId, quantity) {
    function openDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) {
        drawer.open();
      }
    }

    // CartHandler.add() opens the drawer ITSELF after the fetch — chaining a
    // second openDrawer() here re-ran open() before the (async) 'active' class
    // landed, re-saving the scroll position with the body already fixed
    // (scrollY 0) and dumping the user at the top of the page.
    if (window.CartHandler && typeof window.CartHandler.add === 'function') {
      window.CartHandler.add(variantId, quantity)
        .catch((err) => console.error('Search add to cart failed', err));
      return;
    }

    // Fallback: request the cart-drawer section so we can render + open it.
    const drawer = document.querySelector('cart-drawer');
    const body = { id: variantId, quantity: quantity };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) throw data;
        if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
          drawer.renderContents(data); // renders + opens the drawer
        } else {
          openDrawer();
        }
      })
      .catch((err) => console.error('Search add to cart failed', err));
  }

  return html`
    <div id="hits-products" ref=${containerRef}>
      ${useOurCards ? html`
        ${window.PlpToolbar ? html`
          <${window.PlpToolbar}
            totalResults=${helper.lastResults?.nbHits || hits.length}
            viewMode=${viewMode}
            sortBy=${sortBy}
            hitsPerPage=${96}
            pageType=${window.algoliaCollectionContext ? 'collection' : 'search'}
            searchQuery=${new URLSearchParams(window.location.search).get('q') || ''}
            collectionTitle=${window.algoliaCollectionContext?.title || ''}
            onToggleView=${toggleView}
            onSortChange=${handleSortChange}
            onHitsPerPageChange=${function(){}}
            onOpenFilters=${function(){}}
          />
        ` : html`<${ViewToggle} viewMode=${viewMode} onToggle=${toggleView} />`}
        ${viewMode === 'list' && ListCard ? html`
          <div class="plp-grid plp-grid--list">
            ${displayedHits.map(hit => html`
              <${ListCard}
                key=${hit.objectID}
                product=${adaptHit(hit)}
                onAddToCart=${handleAddToCart}
              />
            `)}
          </div>
        ` : html`
          <div class="plp-grid plp-grid--grid">
            ${displayedHits.map(hit => html`
              <${GridCard}
                key=${hit.objectID}
                product=${adaptHit(hit)}
                onAddToCart=${handleAddToCart}
              />
            `)}
          </div>
        `}
      ` : html`
        <ul class="ais-Hits-list">
          ${hits.map(hit => html`
            <li key=${hit.objectID} class="ais-Hits-item">
              <${ProductCard} hit=${hit} activeFacetAttrs=${activeFacetAttrs} />
            </li>
          `)}
        </ul>
      `}
      ${hasMore && html`
        <div id="ais-infinite-scroll-sentinel">
          <div class="ais-infinite-spinner">
            <span class="ais-infinite-spinner-dot" />
            <span class="ais-infinite-spinner-dot" />
            <span class="ais-infinite-spinner-dot" />
          </div>
        </div>
      `}
    </div>
  `;
}

// --- Algolia Setup ---
const GEO_CONTEXTS = ["hartville_store", "middlefield_store", "ecom"];

function patchClientForGeoContext(client) {
  if (!client || client._geoWrapped) return;
  client._geoWrapped = true;
  const _orig = client.search.bind(client);
  client.search = function(requests) {
    const geoContext = getGeoRadius("hartville") ? "hartville_store"
      : getGeoRadius("middlefield") ? "middlefield_store"
      : "ecom";
    return _orig(requests.map(req => {
      const existing = Array.isArray(req.params?.ruleContexts) ? req.params.ruleContexts : [];
      const merged = [...existing.filter(c => !GEO_CONTEXTS.includes(c)), geoContext];
      return { ...req, params: { ...req.params, ruleContexts: merged } };
    }));
  };
}

document.addEventListener("algolia.hooks.initialize", () => {
  // --- Custom URL routing ---
  // The Algolia Shopify plugin creates its own router middleware and ignores
  // routing config via beforeInstantSearchOptions. The search instance doesn't
  // exist yet during algolia.hooks.initialize, so we can't patch start().
  //
  // Strategy:
  // 1. beforeInstantSearchConfigurationOptions: inject URL filters into the
  //    first helper.search() call so the very first API request is filtered.
  // 2. In afterInstantSearchStart: patch the router's onStateChange to write
  //    clean URL params (stateToRoute) when facets change.
  // 3. In onRender (first render): apply URL state from clean params via
  //    setUiState since the plugin's default routeToState doesn't map them
  //    to the correct uiState structure.

  // Layer 2 — Intercept the first search to inject URL facet filters.
  // initialUiState is ignored when the plugin's routing is active, so we inject
  // refinements directly on the helper before the first helper.search() call.
  if (urlHasFacetParams()) {
    algoliaShopify.hooks.registerHook("beforeInstantSearchConfigurationOptions", (options) => {
      const originalSearchFunction = options.searchFunction;
      let _firstSearch = true;

      options.searchFunction = function(helper) {
        if (_firstSearch) {
          _firstSearch = false;
          const uiState = _buildUiStateFromUrl();
          if (uiState?.refinementList) {
            // Apply each refinement with the operator the facet widget uses.
            // vendor/product_type/tags are registered "conjunctive" (AND →
            // facetsRefinements); price_range etc. are "disjunctive" (OR →
            // disjunctiveFacetsRefinements). Using the wrong list filters the
            // results but leaves the facet checkbox unchecked (only the active-
            // filter chip shows), so match the widget's operator here.
            const facetCfg = algoliaShopify?.config?.facets || [];
            const conjunctive = {};
            for (const f of facetCfg) {
              const a = f.attribute || f.name;
              if (a && f.type === 'conjunctive') conjunctive[a] = true;
            }
            for (const [attr, vals] of Object.entries(uiState.refinementList)) {
              if (conjunctive[attr]) {
                if (!helper.state.facets?.includes(attr)) {
                  helper.state.facets = [...(helper.state.facets || []), attr];
                }
                for (const v of vals) {
                  helper.addFacetRefinement(attr, v);
                }
              } else {
                if (!helper.state.disjunctiveFacets?.includes(attr)) {
                  helper.state.disjunctiveFacets = [...(helper.state.disjunctiveFacets || []), attr];
                }
                for (const v of vals) {
                  helper.addDisjunctiveFacetRefinement(attr, v);
                }
              }
            }
          }
          if (uiState?.hierarchicalMenu) {
            for (const [attr, vals] of Object.entries(uiState.hierarchicalMenu)) {
              if (vals?.[0]) helper.toggleFacetRefinement(attr, vals[0]);
            }
          }
          if (uiState?.query) helper.setQuery(uiState.query);
        }
        if (originalSearchFunction) {
          originalSearchFunction(helper);
        } else {
          helper.search();
        }
      };

      return options;
    });
  }

  algoliaShopify.hooks.registerHook(
    "afterInstantSearchStartRemoveDefaultWidgets",
    (defaultWidgets) => {
      patchClientForGeoContext(algoliaShopify?.instantsearch?.search?.helper?.client);

      // Push the unified userToken (theme.liquid bootstrap) into the Insights SDK
      // so the plugin's own events use the same token as ours and the custom pixel.
      if (typeof window.aa === 'function' && window.__algoliaUserToken) {
        window.aa('setUserToken', window.__algoliaUserToken);
      }

      // Patch the plugin's router onStateChange to write clean URLs
      const search = algoliaShopify?.instantsearch?.search;
      const routerMw = search?.middleware?.find(m => m.instance?.$$type?.includes('router'));
      if (routerMw) {
        const indexName = search.indexName || `${algoliaShopify?.config?.index_prefix || 'shopify_'}products`;
        function attrToParam(attr) { return _ATTR_TO_PARAM[attr] || attr.split('.').pop(); }


        let lastRouteState;
        let writeTimer;
        let isFirstWrite = true;
        // Use module-scope _initialUrlParams — by this point the router may have
        // already normalised the URL, stripping our custom clean params.
        const initialParams = _initialUrlParams;
        routerMw.instance.onStateChange = function({ uiState }) {
          if (_suppressUrlWrite) return;
          const s = uiState[indexName] || {};
          const route = {};
          if (s.query) route.q = s.query;
          if (s.page && s.page > 1) route.page = s.page;
          if (s.hierarchicalMenu) {
            for (const [attr, vals] of Object.entries(s.hierarchicalMenu)) {
              if (vals?.length) route[attrToParam(attr)] = vals.join(' > ');
            }
          }
          if (s.refinementList) {
            for (const [attr, vals] of Object.entries(s.refinementList)) {
              if (attr === _RATING_ATTR) continue; // collapsed to one integer below
              if (vals?.length) route[attrToParam(attr)] = vals;
            }
          }
          if (s.range) {
            for (const [attr, range] of Object.entries(s.range)) {
              route[attrToParam(attr)] = range;
            }
          }
          if (s.menu) {
            for (const [attr, val] of Object.entries(s.menu)) {
              if (val) route[attrToParam(attr)] = val;
            }
          }
          // Include hidden facet refinements applied via helper (not in uiState)
          const helper = search?.helper;
          if (helper) {
            const dFacetRefinements = helper.state.disjunctiveFacetsRefinements || {};
            for (const [attr, vals] of Object.entries(dFacetRefinements)) {
              if (attr === _RATING_ATTR) continue; // handled explicitly below
              const param = attrToParam(attr);
              if (vals?.length && !route[param]) {
                route[param] = vals;
              }
            }
          }
          // Rating: collapse the set of decimal values to one cumulative integer
          // threshold so the URL reads ?rating=4 (= 4★ & Up) instead of a long
          // comma-joined JSON list.
          {
            const rVals = (s.refinementList && s.refinementList[_RATING_ATTR])
              || helper?.state?.disjunctiveFacetsRefinements?.[_RATING_ATTR]
              || [];
            if (rVals.length) {
              const t = _ratingThresholdFromValues(rVals);
              if (t != null) route[attrToParam(_RATING_ATTR)] = [String(t)];
            }
          }
          if (lastRouteState && JSON.stringify(lastRouteState) === JSON.stringify(route)) return;
          lastRouteState = route;
          // Debounce URL writes (matches plugin's default 400ms writeDelay)
          clearTimeout(writeTimer);
          writeTimer = setTimeout(() => {
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(route)) {
              // Join multi-value facets with comma instead of repeated params
              // because Shopify's server-side routing deduplicates repeated params.
              if (Array.isArray(v)) qs.set(k, v.join(','));
              else qs.set(k, String(v));
            }
            // Build the set of params Algolia manages (all facet attributes plus
            // q/page/category). A *cleared* facet is absent from `route`, so the
            // preserve-loop below must NOT re-add its stale value from the current
            // URL — otherwise the filter reappears after a reload.
            const managedParams = new Set(['q', 'page', 'category']);
            if (helper && helper.state) {
              [].concat(
                helper.state.facets || [],
                helper.state.disjunctiveFacets || [],
                (helper.state.hierarchicalFacets || []).map((f) => f.name)
              ).forEach((attr) => managedParams.add(attrToParam(attr)));
            }
            Object.keys(_paramToAttr).forEach((p) => managedParams.add(p));

            // Preserve only unmanaged params (UTMs, tracking, etc.); a managed
            // facet param missing from `route` was intentionally cleared.
            const currentParams = new URLSearchParams(window.location.search);
            for (const [k, v] of currentParams.entries()) {
              if (managedParams.has(k)) continue;
              if (!(k in route) && !qs.has(k)) { qs.append(k, v); }
            }
            // On early writes, preserve initial clean params not yet in route state.
            if (!_urlStateApplied) {
              for (const [k, v] of initialParams.entries()) {
                if (!(k in route) && !qs.has(k)) { qs.set(k, v); }
              }
            }
            const newUrl = window.location.pathname + (qs.toString() ? '?' + qs.toString() : '');
            if (isFirstWrite) {
              window.history.replaceState(null, '', newUrl);
              isFirstWrite = false;
            } else {
              window.history.pushState(null, '', newUrl);
            }
          }, 400);
        };
      }

      const hitsWidget = defaultWidgets.find(w => w.$$widgetType === "ais.hits");
      const paginationWidget = defaultWidgets.find(w => w.$$widgetType === "ais.pagination");
      return [hitsWidget, paginationWidget].filter(Boolean);
    }
  );

  // Inject URL facet filters into the first real search via searchFunction.
  // DynamicWidgets fires a discovery query (hitsPerPage:0) first — we let that
  // through. On the first real query (hitsPerPage > 0), we inject URL refinements
  // into helper.state so the search goes out already filtered.
  algoliaShopify.hooks.registerHook("beforeInstantSearchStartAddWidgets", () => {
    // Patch _mainHelperSearch to inject URL facet filters into the first real
    // search. This runs before search.start() and intercepts the search call.
    if (urlHasFacetParams()) {
      const search = algoliaShopify?.instantsearch?.search;
      if (search && typeof search._mainHelperSearch === 'function') {
        const _origMainSearch = search._mainHelperSearch.bind(search);
        let _filtersInjected = false;
        search._mainHelperSearch = function() {
          if (!_filtersInjected) {
            _filtersInjected = true;
            const helper = search.helper || search.mainHelper;
            if (helper) {
              const uiState = _buildUiStateFromUrl();
              if (uiState?.refinementList) {
                for (const [attr, vals] of Object.entries(uiState.refinementList)) {
                  if (!helper.state.disjunctiveFacets?.includes(attr)) {
                    helper.state.disjunctiveFacets = [...(helper.state.disjunctiveFacets || []), attr];
                  }
                  for (const v of vals) {
                    helper.addDisjunctiveFacetRefinement(attr, v);
                  }
                }
              }
              if (uiState?.query) helper.setQuery(uiState.query);
            }
          }
          return _origMainSearch();
        };
      }
    }

    const { configure } = algoliaShopify.externals.widgets;
    const params = { hitsPerPage: 96, clickAnalytics: true, removeWordsIfNoResults: "allOptional" };
    if (window.__algoliaUserToken) params.userToken = window.__algoliaUserToken;
    return [configure(params)];
  });
});

// Explicit attribute <-> clean-param mappings. Entries here cover attributes whose
// last dot-segment differs from the URL param we write (e.g. ...lvl0 -> category)
// and hidden facets that may not appear in helperState.
const _ATTR_TO_PARAM = { 'meta.custom.algolia_categories.categories.lvl0': 'category' };
const _PARAM_TO_ATTR = {};
for (const [attr, param] of Object.entries(_ATTR_TO_PARAM)) {
  _PARAM_TO_ATTR[param] = attr;
}

// Reverse mapping extended at runtime from registered facets
// e.g. { brand: 'meta.specification.brand', parent_color: 'meta.custom.parent_color' }
let _paramToAttr = { ..._PARAM_TO_ATTR };

// ── Rating facet: cumulative "& Up" integer buckets ──────────────────────────
// The Algolia index facets `meta.reviews.rating` as exact decimal averages (one
// value per 4.5 / 4.6 / 4.7 …). snippets/algolia-rating-stars.liquid hides those
// rows and renders cumulative integer buckets ("4★ & Up" = every product rated
// ≥ 4). It refines through the RefinementList widget via setUiState (so the
// widget stays authoritative). To keep the URL readable we serialise the rating
// refinement as a single integer threshold (?rating=4) on write, and on read we
// stash that threshold in window.__lhRatingPendingThreshold for the snippet to
// expand back to the matching decimal values once results (hence the values) load.
const _RATING_ATTR = 'meta.reviews.rating';
function _ratingValueToNumber(v) {
  try { const o = JSON.parse(v); if (o && o.value != null) return parseFloat(o.value); } catch (e) {}
  const m = String(v).match(/"value"\s*:\s*"?([0-9.]+)/);
  return m ? parseFloat(m[1]) : NaN;
}
// Cumulative bucket = floor of the smallest refined rating (e.g. {4.5,4.8} → 4).
function _ratingThresholdFromValues(values) {
  let min = Infinity;
  for (const v of values) { const n = _ratingValueToNumber(v); if (!isNaN(n) && n < min) min = n; }
  return min === Infinity ? null : Math.floor(min);
}
window.__lhRatingPendingThreshold = null;

// "Selected filters" (ais-current-refined-values) chips render the raw
// meta.reviews.rating facet value as its JSON string (e.g. `: {"scale_min":...}`).
// Replace that text with the plain rating number ("4.5 Stars" — no star glyph,
// matching the facet's text-only design), on every native widget re-render.
function formatRatingRefinementChips() {
  document.querySelectorAll('.ais-current-refined-values--link[data-attribute="' + _RATING_ATTR + '"]').forEach(link => {
    const wrap = link.querySelector('div');
    if (!wrap) return;
    for (let node = wrap.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.indexOf('{') !== -1) {
        const n = _ratingValueToNumber(node.textContent);
        if (!isNaN(n)) node.textContent = ': ' + n.toFixed(1) + ' Stars';
        break;
      }
    }
  });
}
document.addEventListener("algolia.instantSearch.onRender", formatRatingRefinementChips);
// API the rating snippet calls. setValues/clear go through the widget's uiState
// (reliable) rather than the helper, so the RefinementList stays authoritative.
window.__lhRatingFacet = {
  attr: _RATING_ATTR,
  _search() { return window.algoliaShopify?.instantsearch?.search || null; },
  setValues(values) {
    const search = this._search();
    if (!search) return;
    const index = search.indexName;
    search.setUiState((prev) => {
      const cur = prev[index] || {};
      const rl = Object.assign({}, cur.refinementList);
      if (values && values.length) rl[_RATING_ATTR] = values.slice();
      else delete rl[_RATING_ATTR];
      return Object.assign({}, prev, { [index]: Object.assign({}, cur, { refinementList: rl, page: 1 }) });
    });
    window.__lhRatingPendingThreshold = null;
  },
  clear() { this.setValues([]); },
  // Current cumulative threshold from the widget's uiState, else the pending one.
  currentThreshold() {
    const search = this._search();
    if (search && typeof search.getUiState === 'function') {
      const ui = search.getUiState()[search.indexName] || {};
      const refs = (ui.refinementList && ui.refinementList[_RATING_ATTR]) || [];
      if (refs.length) return _ratingThresholdFromValues(refs);
    }
    return window.__lhRatingPendingThreshold;
  }
};

// Build a uiState object from URL params. Used both by beforeInstantSearchOptions
// (for initialUiState) and applyUrlStateOnFirstRender (fallback).
function _buildUiStateFromUrl(indexName) {
  const qs = _initialUrlParams;
  if (!urlHasFacetParams()) return null;
  // Extend mapping from plugin config (helper not available yet at options time)
  const pluginFacets = algoliaShopify?.config?.facets;
  if (pluginFacets && Array.isArray(pluginFacets)) {
    for (const f of pluginFacets) {
      const attr = f.attribute || f.name;
      if (!attr) continue;
      const shortName = attr.split('.').pop();
      if (shortName !== attr && !_paramToAttr[shortName]) {
        _paramToAttr[shortName] = attr;
      }
    }
  }
  function paramToAttr(param) { return _paramToAttr[param] || param; }
  const RESERVED = new Set(['q', 'page', 'category']);
  const s = {};
  const qVal = qs.get('q');
  if (qVal) s.query = qVal;
  const pageVal = qs.get('page');
  if (pageVal) s.page = Number(pageVal);
  const catVal = qs.get('category');
  if (catVal) {
    s.hierarchicalMenu = { [paramToAttr('category')]: [catVal] };
  }
  const refinementList = {};
  for (const [param, rawVal] of qs.entries()) {
    if (RESERVED.has(param)) continue; // q/page/category handled above
    // Allowlist: only params that map to a real Algolia facet become refinements.
    // Tracking junk (_kx/kx, utm_*, fbclid, gclid, and any future param) is ignored.
    if (!isKnownFacetParam(param)) continue;
    const attr = paramToAttr(param);
    if (attr === _RATING_ATTR) {
      // ?rating=N is a cumulative threshold, not a literal facet value. Stash it;
      // the rating snippet expands it to the matching decimal values once known.
      const t = parseInt(rawVal, 10);
      if (!isNaN(t)) window.__lhRatingPendingThreshold = t;
      continue;
    }
    const values = rawVal.includes(',') ? rawVal.split(',').map(v => v.trim()).filter(Boolean) : [rawVal];
    for (const val of values) {
      if (!refinementList[attr]) refinementList[attr] = [];
      refinementList[attr].push(val);
    }
  }
  if (Object.keys(refinementList).length) s.refinementList = refinementList;
  if (!s.query && !s.page && !s.hierarchicalMenu && !s.refinementList) return null;
  return s;
}

// Allowlist of URL params the parser turns into Algolia state. Built from the
// plugin's facet config (full attribute + short name), the runtime reverse map,
// and the custom widgets that own a param without a dotted attribute (category,
// price). This is an allowlist on purpose: ANY param not in here — Klaviyo's
// _kx/kx, utm_*, fbclid, gclid, or any future tracking param — is left untouched.
// It is never parsed as a refinement (so it can't break PLP/search parsing) yet
// still survives in the URL via the write-side managedParams preservation.
// Guard Algolia's params, leave everything else alone.
const _BASE_FACET_PARAMS = ['category', 'price', 'price_range', 'vendor', 'brand', 'tags', 'features'];
function knownFacetParams() {
  const set = new Set(_BASE_FACET_PARAMS);
  const cfg = algoliaShopify?.config?.facets;
  if (Array.isArray(cfg)) {
    for (const f of cfg) {
      const attr = f && (f.attribute || f.name);
      if (!attr) continue;
      set.add(attr);
      set.add(attr.split('.').pop()); // short name (the clean URL param we write)
    }
  }
  for (const p of Object.keys(_paramToAttr)) set.add(p); // runtime-registered facets
  return set;
}
function isKnownFacetParam(param) { return knownFacetParams().has(param); }

function urlHasFacetParams() {
  const known = knownFacetParams();
  return [..._initialUrlParams.keys()].some(k => known.has(k));
}

let _suppressUrlWrite = false;
let _urlStateApplied = false;
function applyUrlStateOnFirstRender() {
  if (_urlStateApplied) return;
  _urlStateApplied = true;
  // Use params captured at module load — the plugin's router may have already
  // normalised the URL and stripped params it doesn't recognise.
  const qs = _initialUrlParams;
  // Skip if no facet params in URL (avoids unnecessary second search)
  if (!urlHasFacetParams()) return;
  const search = algoliaShopify?.instantsearch?.search;
  if (!search) return;
  const indexName = search.indexName;
  // Extend reverse mapping with dynamically registered facets (covers visible widgets)
  const helperState = search.helper?.state;
  if (helperState) {
    const allFacets = [
      ...(helperState.facets || []),
      ...(helperState.disjunctiveFacets || []),
      ...(helperState.hierarchicalFacets?.map(f => f.name) || []),
    ];
    for (const attr of allFacets) {
      const shortName = attr.split('.').pop();
      if (shortName !== attr && !_paramToAttr[shortName]) {
        _paramToAttr[shortName] = attr;
      }
    }
  }
  // Also scan Algolia plugin's facet config for hidden/unregistered facets
  // so they can still be resolved from URL params.
  const pluginFacets = algoliaShopify?.config?.facets;
  // Build a set of hidden facet attributes so we can apply them via the helper
  // instead of setUiState (hidden facets have no widget, so setUiState ignores them).
  const hiddenFacetAttrs = new Set();
  if (pluginFacets && Array.isArray(pluginFacets)) {
    for (const f of pluginFacets) {
      const attr = f.attribute || f.name;
      if (!attr) continue;
      const shortName = attr.split('.').pop();
      if (shortName !== attr && !_paramToAttr[shortName]) {
        _paramToAttr[shortName] = attr;
      }
      if (f.type === 'hidden') hiddenFacetAttrs.add(attr);
    }
  }
  function paramToAttr(param) { return _paramToAttr[param] || param; }
  const RESERVED = new Set(['q', 'page', 'category']);
  const s = {};
  const qVal = qs.get('q');
  if (qVal) s.query = qVal;
  const pageVal = qs.get('page');
  if (pageVal) s.page = Number(pageVal);
  const catVal = qs.get('category');
  if (catVal) {
    s.hierarchicalMenu = { [paramToAttr('category')]: [catVal] };
  }
  const refinementList = {};
  const hiddenRefinements = {}; // collected separately for helper-based application
  for (const [param, rawVal] of qs.entries()) {
    if (RESERVED.has(param)) continue; // q/page/category handled above
    // Allowlist: only params that map to a real Algolia facet become refinements.
    // Tracking junk (_kx/kx, utm_*, fbclid, gclid, and any future param) is ignored.
    if (!isKnownFacetParam(param)) continue;
    const attr = paramToAttr(param);
    if (attr === _RATING_ATTR) {
      // ?rating=N is a cumulative threshold, not a literal facet value. Stash it;
      // the rating snippet expands it to the matching decimal values once known.
      const t = parseInt(rawVal, 10);
      if (!isNaN(t)) window.__lhRatingPendingThreshold = t;
      continue;
    }
    // Split comma-separated values (Shopify deduplicates repeated params via redirect,
    // so multi-value facets are encoded as ?param=val1,val2)
    const values = rawVal.includes(',') ? rawVal.split(',').map(v => v.trim()).filter(Boolean) : [rawVal];
    for (const val of values) {
      if (hiddenFacetAttrs.has(attr)) {
        if (!hiddenRefinements[attr]) hiddenRefinements[attr] = [];
        hiddenRefinements[attr].push(val);
      } else {
        if (!refinementList[attr]) refinementList[attr] = [];
        refinementList[attr].push(val);
      }
    }
  }
  if (Object.keys(refinementList).length) s.refinementList = refinementList;
  // Suppress URL writes while applying initial state to prevent partial/incorrect
  // URL rewrites from racing onStateChange calls.
  _suppressUrlWrite = true;
  // Only apply if there's actual facet state (not just UTMs)
  if (s.query || s.page || s.hierarchicalMenu || s.refinementList) {
    search.setUiState({ [indexName]: s });
  }
  // Apply hidden facet refinements directly via the helper since they have no
  // widget and setUiState would silently ignore them.
  const helper = search.helper;
  if (helper && Object.keys(hiddenRefinements).length) {
    for (const [attr, vals] of Object.entries(hiddenRefinements)) {
      // Ensure the attribute is registered as a disjunctive facet for filtering
      if (!helper.state.disjunctiveFacets?.includes(attr)) {
        helper.state.disjunctiveFacets = [...(helper.state.disjunctiveFacets || []), attr];
      }
      for (const v of vals) {
        helper.addDisjunctiveFacetRefinement(attr, v);
      }
    }
    helper.search();
  }
  // Lift suppression synchronously — the URL write is debounced (400ms) so
  // the next onStateChange with correct state will write the full URL.
  _suppressUrlWrite = false;
}

// Render accumulated hits with infinite scroll via Preact InfiniteHits component.
let accumulatedHits = [];
let currentSearchPage = 0;
let _renderCount = 0;

document.addEventListener("algolia.instantSearch.onRender", () => {
  _renderCount++;
  const helper = algoliaShopify?.instantsearch?.search?.helper;
  const results = helper?.lastResults;
  if (!results) return;

  // Honor Algolia redirect rules before rendering results
  const redirectUrl = results.renderingContent?.redirect?.url
    || (Array.isArray(results.userData) ? results.userData.find(d => d?.redirect)?.redirect : results.userData?.redirect)
    || null;
  if (redirectUrl) { window.location.href = redirectUrl; return; }

  applyUrlStateOnFirstRender();

  // Skip rendering unfiltered results on first render when URL has facet params.
  // The second render (after applyUrlStateOnFirstRender triggers the filtered search)
  // will have the correct results.
  if (_renderCount === 1 && urlHasFacetParams()) return;

  // --- Custom breadcrumb from hierarchical refinement state ---
  const breadcrumbContainer = document.querySelector('#algolia-breadcrumb-widget');
  if (breadcrumbContainer) {
    const FACET_NAME = 'meta.custom.algolia_categories.categories.lvl0';
    const refinements = helper.state.hierarchicalFacetsRefinements[FACET_NAME] || [];
    const path = refinements[0] || '';
    if (path) {
      sessionStorage.setItem('algolia_breadcrumb_path', path);
    } else {
      sessionStorage.removeItem('algolia_breadcrumb_path');
    }
    const collectionCtx = window.algoliaCollectionContext;
    if (collectionCtx) {
      sessionStorage.setItem('algolia_breadcrumb_collection_url', collectionCtx.url);
      sessionStorage.setItem('algolia_breadcrumb_collection_title', collectionCtx.title);
      // Mega-menu ancestor chain — read by the PDP breadcrumb so it shows the
      // same trail the collection PLP showed.
      sessionStorage.setItem('algolia_breadcrumb_collection_parents', JSON.stringify(collectionCtx.parents || []));
    } else {
      sessionStorage.removeItem('algolia_breadcrumb_collection_url');
      sessionStorage.removeItem('algolia_breadcrumb_collection_title');
      sessionStorage.removeItem('algolia_breadcrumb_collection_parents');
    }
    const sq = helper.state.query || '';
    if (sq && !collectionCtx) {
      sessionStorage.setItem('algolia_breadcrumb_search_query', sq);
    } else {
      sessionStorage.removeItem('algolia_breadcrumb_search_query');
    }
    // Store the current PLP URL with all clean filter params so PDP
    // breadcrumb links can navigate back with the full filter state.
    sessionStorage.setItem('algolia_breadcrumb_plp_url', window.location.pathname + window.location.search);
    const levels = path ? path.split(' > ') : [];

    const nav = document.createElement('nav');
    nav.className = 'breadcrumbs';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'breadcrumbs');

    const ol = document.createElement('ol');
    ol.className = 'breadcrumbs__list';

    // Home — always navigates to /
    const homeLi = document.createElement('li');
    homeLi.className = 'breadcrumbs__item';
    const homeA = document.createElement('a');
    homeA.className = 'breadcrumbs__link';
    homeA.href = '/';
    homeA.textContent = 'Home';
    homeLi.appendChild(homeA);
    ol.appendChild(homeLi);

    // Search query crumb (only on search pages, no collection context)
    if (!collectionCtx && sq) {
      const sqSep = document.createElement('li');
      sqSep.className = 'breadcrumbs__separator';
      sqSep.setAttribute('aria-hidden', 'true');
      sqSep.textContent = '/';
      ol.appendChild(sqSep);

      const sqLi = document.createElement('li');
      sqLi.className = 'breadcrumbs__item';

      if (levels.length) {
        const sqA = document.createElement('a');
        sqA.className = 'breadcrumbs__link';
        sqA.href = '/search?q=' + encodeURIComponent(sq);
        sqA.textContent = '"' + sq + '"';
        sqA.addEventListener('click', (e) => {
          e.preventDefault();
          helper.removeHierarchicalFacetRefinement(FACET_NAME).search();
        });
        sqLi.appendChild(sqA);
      } else {
        const sqSpan = document.createElement('span');
        sqSpan.className = 'breadcrumbs__link';
        sqSpan.setAttribute('aria-current', 'page');
        sqSpan.textContent = '"' + sq + '"';
        sqLi.appendChild(sqSpan);
      }
      ol.appendChild(sqLi);
    }

    // Parent collection crumbs (mega-menu hierarchy, exposed by
    // algolia-native-config.liquid as collectionCtx.parents)
    if (collectionCtx && Array.isArray(collectionCtx.parents)) {
      for (const parent of collectionCtx.parents) {
        if (!parent || !parent.title) continue;
        const pSep = document.createElement('li');
        pSep.className = 'breadcrumbs__separator';
        pSep.setAttribute('aria-hidden', 'true');
        pSep.textContent = '/';
        ol.appendChild(pSep);

        const pLi = document.createElement('li');
        pLi.className = 'breadcrumbs__item';
        const hasUrl = parent.url && parent.url !== '#';
        if (hasUrl) {
          const pA = document.createElement('a');
          pA.className = 'breadcrumbs__link';
          pA.href = parent.url;
          pA.textContent = parent.title;
          pLi.appendChild(pA);
        } else {
          // Mega-menu headers without a real destination stay unclickable.
          const pSpan = document.createElement('span');
          pSpan.className = 'breadcrumbs__link';
          pSpan.textContent = parent.title;
          pLi.appendChild(pSpan);
        }
        ol.appendChild(pLi);
      }
    }

    // Collection crumb (only on collection pages)
    if (collectionCtx) {
      const collSep = document.createElement('li');
      collSep.className = 'breadcrumbs__separator';
      collSep.setAttribute('aria-hidden', 'true');
      collSep.textContent = '/';
      ol.appendChild(collSep);

      const collLi = document.createElement('li');
      collLi.className = 'breadcrumbs__item';

      if (levels.length) {
        const collA = document.createElement('a');
        collA.className = 'breadcrumbs__link';
        collA.href = collectionCtx.url;
        collA.textContent = collectionCtx.title;
        collA.addEventListener('click', (e) => {
          e.preventDefault();
          helper.removeHierarchicalFacetRefinement(FACET_NAME).search();
        });
        collLi.appendChild(collA);
      } else {
        const collSpan = document.createElement('span');
        collSpan.className = 'breadcrumbs__link';
        collSpan.setAttribute('aria-current', 'page');
        collSpan.textContent = collectionCtx.title;
        collLi.appendChild(collSpan);
      }
      ol.appendChild(collLi);
    }

    levels.forEach((label, i) => {
      const sep = document.createElement('li');
      sep.className = 'breadcrumbs__separator';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '/';
      ol.appendChild(sep);

      const li = document.createElement('li');
      li.className = 'breadcrumbs__item';

      if (i === levels.length - 1) {
        const span = document.createElement('span');
        span.className = 'breadcrumbs__link';
        span.setAttribute('aria-current', 'page');
        span.textContent = label;
        li.appendChild(span);
      } else {
        const a = document.createElement('a');
        a.className = 'breadcrumbs__link';
        a.href = '#';
        a.textContent = label;
        const refinePath = levels.slice(0, i + 1).join(' > ');
        a.addEventListener('click', (e) => {
          e.preventDefault();
          helper.removeHierarchicalFacetRefinement(FACET_NAME)
            .addHierarchicalFacetRefinement(FACET_NAME, refinePath)
            .search();
        });
        li.appendChild(a);
      }
      ol.appendChild(li);
    });

    nav.appendChild(ol);
    breadcrumbContainer.innerHTML = '';
    breadcrumbContainer.appendChild(nav);
  }

  styleHierarchicalMenu();

  const container = document.querySelector(".ais-hits-container");
  if (!container) return;

  const page = results.page ?? 0;
  const hits = results.hits ?? [];
  const hasMore = page < results.nbPages - 1;

  // Inject Algolia Insights tracking metadata onto each hit
  const queryID = results.queryID;
  const hitsPerPage = results.hitsPerPage || 96;
  hits.forEach((hit, i) => {
    hit.__queryID = queryID;
    hit.__position = page * hitsPerPage + i + 1;
  });

  if (page === 0) {
    accumulatedHits = [...hits];
    currentSearchPage = 0;
  } else if (page > currentSearchPage) {
    accumulatedHits = [...accumulatedHits, ...hits];
    currentSearchPage = page;
  } else {
    return; // duplicate render — skip
  }

  render(html`<${InfiniteHits} hits=${accumulatedHits} hasMore=${hasMore} helper=${helper} activeFacetAttrs=${getActiveFacetAttributes()} />`, container);

  // Update results count
  var rcEl = document.querySelector('.algolia-results-count');
  if (rcEl && results) {
    var nb = results.nbHits || 0;
    rcEl.textContent = nb.toLocaleString() + ' Result' + (nb !== 1 ? 's' : '');
  }

  setupViewToggle();
  replaceToggleIcons();
  refreshYotpo();

  const algoliaBreadcrumbs = document.getElementById("algolia-breadcrumbs");
  const isContainer = document.getElementById("algolia-shopify-instantsearch");
  function onFacetsReady() {
    if (algoliaBreadcrumbs) algoliaBreadcrumbs.style.setProperty("display", "flex", "important");
    if (isContainer) isContainer.classList.add("ais-facets-ready");

    // Reveal facets
    var facetsEl = document.querySelector('.ais-facets');
    if (facetsEl) facetsEl.classList.add('is-ready');

    // Desktop: the breadcrumb widget gets its OWN full-width row at the top of
    // .ais-page (grid row 1, spanning both columns) so long paths never wrap.
    // Facets keep the left column, which now starts on row 2 — the same row as
    // .ais-block — so the banner (or the toolbar when there is no banner)
    // top-aligns with the facets.
    var bcWidget = document.getElementById('algolia-breadcrumb-widget');
    var aisPage = document.querySelector('.ais-page');
    if (bcWidget && aisPage && window.innerWidth > 989 && bcWidget.parentNode !== aisPage) {
      aisPage.insertBefore(bcWidget, aisPage.firstChild);
    }
    // Moving the widget out can leave #algolia-breadcrumbs EMPTY (collection
    // desktop). Its inline display:flex !important beats both the desktop hide
    // rule and base.css div:empty, so the empty div stays in the toolbar title
    // group's flow and its 10px flex gap floats the title off the top edge.
    // Hide it for real; mobile keeps the widget inside, so it never hits this.
    if (algoliaBreadcrumbs && algoliaBreadcrumbs.children.length === 0) {
      algoliaBreadcrumbs.style.setProperty('display', 'none', 'important');
    }
    if (facetsEl && facetsEl.parentNode && !document.querySelector('.ais-left-column') && window.innerWidth > 989) {
      var leftCol = document.createElement('div');
      leftCol.className = 'ais-left-column';
      facetsEl.parentNode.insertBefore(leftCol, facetsEl);
      leftCol.appendChild(facetsEl);
    }

    // Move banner into .ais-block (right column) as first child
    var banner = document.querySelector('.acb-banner');
    var aisBlock = document.querySelector('.ais-block');
    if (banner && aisBlock && banner.parentNode !== aisBlock) {
      aisBlock.insertBefore(banner, aisBlock.firstChild);
    }
  }
  if (document.querySelector('#hits-products') || document.querySelector('.ais-facets.is-ready')) {
    onFacetsReady();
  } else {
    var bcObserver = new MutationObserver(function () {
      if (document.querySelector('#hits-products') || document.querySelector('.ais-facets.is-ready')) {
        onFacetsReady();
        bcObserver.disconnect();
      }
    });
    bcObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  fixAlgoliaSortLabel();
});

function initSearchPillStyles() {
  const applyStyles = () => {
    document.querySelectorAll('.header__search-pill .aa-Form').forEach(el => {
      el.style.setProperty('background', 'transparent', 'important');
      el.style.setProperty('border', 'none', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
      el.style.setProperty('border-radius', '0', 'important');
      el.style.setProperty('outline', 'none', 'important');
    });
    document.querySelectorAll('.header__search-pill .aa-InputWrapperPrefix').forEach(el => {
      el.style.setProperty('display', 'none', 'important');
    });
    document.querySelectorAll('.header__search-pill .aa-InputWrapperSuffix').forEach(el => {
      el.style.setProperty('display', 'none', 'important');
    });
    document.querySelectorAll('.search-pill__autocomplete .aa-Input').forEach(input => {
      input.style.setProperty('background', 'transparent', 'important');
      input.style.setProperty('border', 'none', 'important');
      input.style.setProperty('box-shadow', 'none', 'important');
      input.style.setProperty('outline', 'none', 'important');
      if (!input.placeholder) input.setAttribute('placeholder', 'What Are You Looking For?');
    });
  };

  applyStyles();

  const headObserver = new MutationObserver(applyStyles);
  headObserver.observe(document.head, { childList: true });

  // Observar body para detectar cuando Algolia monta el aa-Form
  const bodyObserver = new MutationObserver(applyStyles);
  bodyObserver.observe(document.body, { childList: true, subtree: true });

  setTimeout(() => {
    headObserver.disconnect();
    bodyObserver.disconnect();
  }, 8000);
}

async function initSearchPillCategories() {
  const container = document.querySelector('#ais-category-menu');
  if (!container || container.querySelector('.ais-MenuSelect')) return;

  try {
    const index = window.algoliaShopify.searchClient.initIndex('shopify_products');
    const result = await index.searchForFacetValues('collections', '', {
      maxFacetHits: 50,
      sortFacetValuesBy: 'alpha',
    });

    if (!result.facetHits?.length) return;

    const select = document.createElement('select');
    select.className = 'ais-MenuSelect-select';
    select.setAttribute('aria-label', 'Filter by category');

    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = 'All Categories';
    select.appendChild(defaultOpt);

    result.facetHits.forEach(({ value }) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value;
      select.appendChild(opt);
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'ais-MenuSelect';
    wrapper.appendChild(select);
    container.appendChild(wrapper);
  } catch (e) {
  }
}

function runWhenAlgoliaReady(fn) {
  if (window.algoliaShopify?.helpers_has_initialize) {
    fn();
  } else {
    document.addEventListener('algolia.helpers.initialize', fn, { once: true });
  }
}

runWhenAlgoliaReady(() => {
  initSearchPillStyles();
  initSearchPillCategories();
});

(function setupAlgoliaInstantsearchPreloadVeil() {
  const FALLBACK_MS = 15000;
  const _needsSecondRender = urlHasFacetParams();
  let _firstRenderSeen = false;

  function removePreloadVeil() {
    document.getElementById("algolia-shopify-instantsearch")?.classList.add("algolia-preload-done");
    var b = document.querySelector('.acb-banner');
    if (b) { b.style.visibility = 'visible'; b.style.opacity = '1'; }
  }

  document.addEventListener("algolia.instantSearch.onRender", () => {
    if (!algoliaShopify?.instantsearch?.search?.helper?.lastResults) return;
    // When URL has facet params, the first render shows unfiltered results —
    // wait for the second render (after applyUrlStateOnFirstRender) to remove the veil.
    if (_needsSecondRender && !_firstRenderSeen) {
      _firstRenderSeen = true;
      return;
    }
    setTimeout(removePreloadVeil, 300);
  });

  setTimeout(removePreloadVeil, FALLBACK_MS);
})();

// Style HierarchicalMenu nested items. Applied via JS because
// algolia_custom_styles.css !important rules override CSS-only solutions.
// Uses MutationObserver because the plugin re-renders the menu on each
// facet click, replacing the DOM elements we previously styled.
function styleHierarchicalMenu() {
  document.querySelectorAll('.ais-HierarchicalMenu-item--selected.ais-HierarchicalMenu-item--parent').forEach(item => {
    item.style.setProperty('display', 'flex', 'important');
    item.style.setProperty('flex-direction', 'column', 'important');
    item.style.setProperty('gap', '8px', 'important');
    item.style.setProperty('margin-bottom', '0', 'important');
    item.style.setProperty('list-style', 'none', 'important');
  });
  document.querySelectorAll('.ais-HierarchicalMenu-list--child').forEach(wrapper => {
    wrapper.style.setProperty('padding-left', '1.5rem', 'important');
    wrapper.style.setProperty('width', '100%', 'important');
  });
  document.querySelectorAll('.ais-HierarchicalMenu-list--child .ais-facet--item').forEach(item => {
    item.style.setProperty('margin-bottom', '0', 'important');
  });
}
// Also run on every onRender event (separate listener, not blocked by container check)
document.addEventListener("algolia.instantSearch.onRender", styleHierarchicalMenu);

// ==========================================================
// Mobile filter drawer for Algolia facets
// ==========================================================
(function setupFilterDrawer() {
  // Runs on collections AND search at any width: the native toolbar's
  // onOpenFilters is a no-op, so this drawer is the only way to reach the
  // facets once .ais-left-column is hidden (≤989). The click handler below
  // only reacts to the native toolbar (#hits-products), so the Preact
  // fallback app keeps its own PlpFilterDrawer without doubling up.

  // Create drawer DOM
  var overlay = document.createElement('div');
  overlay.className = 'ais-filter-drawer-overlay';

  var drawer = document.createElement('div');
  drawer.className = 'ais-filter-drawer';
  drawer.innerHTML =
    '<div class="ais-filter-drawer__header">' +
      '<h3 class="ais-filter-drawer__title">Filters</h3>' +
      '<button class="ais-filter-drawer__close" type="button" aria-label="Close filters">&times;</button>' +
    '</div>' +
    '<div class="ais-filter-drawer__body"></div>' +
    '<div class="ais-filter-drawer__footer">' +
      '<button class="ais-filter-drawer__apply" type="button">Apply Filters</button>' +
    '</div>';

  document.body.appendChild(overlay);
  document.body.appendChild(drawer);

  var body = drawer.querySelector('.ais-filter-drawer__body');
  var closeBtn = drawer.querySelector('.ais-filter-drawer__close');
  var applyBtn = drawer.querySelector('.ais-filter-drawer__apply');
  var facetsPlaceholder = document.createComment('ais-facets-placeholder');
  var facetsEl = null;

  function open() {
    facetsEl = document.querySelector('.ais-facets');
    if (facetsEl) {
      facetsEl.parentNode.insertBefore(facetsPlaceholder, facetsEl);
      body.appendChild(facetsEl);
    }
    overlay.classList.add('ais-filter-drawer-overlay--open');
    drawer.classList.add('ais-filter-drawer--open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    overlay.classList.remove('ais-filter-drawer-overlay--open');
    drawer.classList.remove('ais-filter-drawer--open');
    document.body.style.overflow = '';
    if (facetsEl && facetsPlaceholder.parentNode) {
      facetsPlaceholder.parentNode.insertBefore(facetsEl, facetsPlaceholder);
      facetsPlaceholder.remove();
    }
  }

  closeBtn.addEventListener('click', close);
  applyBtn.addEventListener('click', close);
  overlay.addEventListener('click', close);
  document.addEventListener('keydown', function(e) { if (e.key === 'Escape') close(); });

  // Wire to filter button in the NATIVE toolbar only — the Preact fallback
  // toolbar opens its own drawer via onOpenFilters, and without native
  // facets there is nothing to show anyway.
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.plp-toolbar__filter-btn');
    if (!btn || !btn.closest('#hits-products')) return;
    if (!document.querySelector('.ais-facets')) return;
    e.preventDefault();
    open();
  });
})();

})();
